import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { Key, X, PaperPlaneRight, Trash, SignOut, Robot, Paperclip, FilePdf, Image as ImageIcon, ChartBar, CaretUp, CaretDown, Calendar, Upload, CheckCircle, Spinner, PencilSimple } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Calendar as DatePickerCalendar } from "./components/ui/calendar";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ftp_admin_token";
const SESSION_KEY = "ftp_admin_chat_session";

function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function newSessionId() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ============== Race-Time Countdown (mirrors App.js logic) ==============
// V7.3 — Harry asked for the public dashboard's jump-time countdown to
// appear above the runner name on every admin betslip card.  We could
// import from App.js but that would create a circular dependency on
// some module bundlers, so the small set of helpers is mirrored here
// verbatim.  Keep in sync with App.js if the public version ever
// changes.

const STATE_TZ_ADMIN = {
  NSW: "Australia/Sydney", VIC: "Australia/Melbourne",
  QLD: "Australia/Brisbane", WA: "Australia/Perth",
  SA: "Australia/Adelaide", TAS: "Australia/Hobart",
  ACT: "Australia/Sydney", NT: "Australia/Darwin",
  NZ: "Pacific/Auckland",
};

function adminGetOffsetMinutes(tz, atMs) {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = fmt.formatToParts(new Date(atMs));
    const get = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
    let hour = get("hour"); if (hour === 24) hour = 0;
    const trackUtc = Date.UTC(
      get("year"), get("month") - 1, get("day"),
      hour, get("minute"), get("second"),
    );
    return Math.round((trackUtc - atMs) / 60000);
  } catch { return 600; }
}

function adminCountdownToRace(raceTime, state, dateString) {
  if (!raceTime) return null;
  let raceUtcMs;
  // ISO-8601 UTC (e.g. "2026-06-08T06:20:00Z") — admin betslip cards
  // ship the raw `race_time` from the snapshot which is a full
  // timestamp, NOT the "HH:MM" local-track string the public
  // dashboard receives.  Parse as a Date directly when we detect the
  // "T...Z" shape; fall back to the HH:MM logic otherwise.
  if (/T\d{2}:\d{2}/.test(raceTime)) {
    const t = Date.parse(raceTime);
    if (Number.isNaN(t)) return null;
    raceUtcMs = t;
  } else if (raceTime.includes(":")) {
    const [h, m] = raceTime.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const tz = STATE_TZ_ADMIN[state] || "Australia/Melbourne";
    const now = Date.now();
    const trackOffsetMin = adminGetOffsetMinutes(tz, now);
    let raceY, raceM, raceD;
    if (dateString && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [d, mo, y] = dateString.split("/").map(Number);
      raceY = y; raceM = mo - 1; raceD = d;
    } else {
      const trackNow = new Date(now + trackOffsetMin * 60000);
      raceY = trackNow.getUTCFullYear();
      raceM = trackNow.getUTCMonth();
      raceD = trackNow.getUTCDate();
    }
    raceUtcMs = Date.UTC(raceY, raceM, raceD, h, m, 0) - trackOffsetMin * 60000;
  } else {
    return null;
  }
  const diffMs = raceUtcMs - Date.now();
  if (diffMs < 0) return null;
  const mins = Math.floor(diffMs / 60000);
  if (mins > 240) return null;
  if (mins < 1) return "LIVE";
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const AdminRaceCountdown = ({ raceTime, state, dateString, testid }) => {
  const [countdown, setCountdown] = useState(
    () => adminCountdownToRace(raceTime, state, dateString),
  );
  useEffect(() => {
    const t = setInterval(
      () => setCountdown(adminCountdownToRace(raceTime, state, dateString)),
      30000,
    );
    return () => clearInterval(t);
  }, [raceTime, state, dateString]);
  if (!countdown) return null;
  const isUrgent = countdown === "LIVE" || /^\d+m$/.test(countdown);
  return (
    <span data-testid={testid || "admin-race-countdown"} style={{
      fontSize: 10, fontWeight: 700,
      color: isUrgent ? "#e53e3e" : "#888",
      background: isUrgent ? "#fee2e2" : "#f5f5f4",
      padding: "1px 6px", borderRadius: 4,
      letterSpacing: "0.04em",
      display: "inline-block", marginBottom: 4,
    }}>{countdown}</span>
  );
};

// ============== Hidden Key Trigger ==============

export const AdminKeyTrigger = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="admin-key-icon"
    aria-label="Admin login"
    title="Admin"
    style={{
      background: "transparent", border: "none", cursor: "pointer",
      padding: 6, marginTop: 6, opacity: 0.18,
      color: "#888", display: "inline-flex", alignItems: "center",
      transition: "opacity 0.2s ease",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.opacity = 0.7; }}
    onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.18; }}
  >
    <Key size={14} weight="bold" />
  </button>
);

// ============== Analytics Modal ==============

const AnalyticsModal = ({ open, onClose, token, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true); setError("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/analytics/visits`, {
          headers: authHeaders(token),
        });
        setData(data);
      } catch (err) {
        if (err?.response?.status === 401) onLogout();
        setError("Couldn't load analytics.");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  if (!open) return null;
  const totals = data?.totals || {};
  const perDay = data?.per_day || [];
  const maxUnique = Math.max(1, ...perDay.map((d) => d.unique_visitors || 0));

  return (
    <div data-testid="admin-analytics-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 210,
        background: "rgba(20,20,22,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: 20,
          width: "100%", maxWidth: 440,
          maxHeight: "85dvh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          WebkitOverflowScrolling: "touch",
        }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ChartBar size={18} weight="fill" color="#323233" />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#323233" }}>Site Visits</h3>
          </div>
          <button data-testid="analytics-close" onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}>
            <X size={18} weight="bold" />
          </button>
        </div>

        {loading && <div style={{ color: "#888", fontSize: 13 }}>Loading…</div>}
        {error && <div style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div>}

        {!loading && !error && data && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 18 }}>
              {[
                ["Today", "today"],
                ["Last 7 days", "last_7d"],
                ["Last 30 days", "last_30d"],
              ].map(([label, key]) => (
                <div key={key} data-testid={`analytics-${key}`}
                  style={{
                    background: "#f4f4f3", borderRadius: 10, padding: "10px 8px",
                    textAlign: "center",
                  }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#323233", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                    {(totals[key]?.unique_visitors ?? 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                    {(totals[key]?.views ?? 0).toLocaleString()} views
                  </div>
                </div>
              ))}
              <div data-testid="analytics-total-users"
                style={{
                  background: "#f4f4f3", borderRadius: 10, padding: "10px 8px",
                  textAlign: "center",
                }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#323233", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                  {(data?.total_users ?? 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 }}>
                  Users
                </div>
                <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                  signed up
                </div>
              </div>
            </div>

            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
              Daily unique visitors — last 30 days
            </div>
            <div data-testid="analytics-bars"
              style={{
                display: "flex", alignItems: "flex-end", gap: 3,
                height: 110, padding: "16px 0 4px", marginBottom: 6,
                borderBottom: "1px solid #e5e5e4",
              }}>
              {perDay.length === 0 ? (
                <div style={{ color: "#aaa", fontSize: 12 }}>No data yet.</div>
              ) : (
                perDay.map((d) => {
                  const pct = (d.unique_visitors / maxUnique) * 100;
                  return (
                    <div key={d.date} title={`${d.date}: ${d.unique_visitors} unique`}
                      style={{
                        flex: 1, height: "100%",
                        display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "flex-end",
                        position: "relative",
                      }}>
                      <span style={{
                        fontSize: 9, color: "#666", fontVariantNumeric: "tabular-nums",
                        marginBottom: 2, lineHeight: 1,
                      }}>
                        {d.unique_visitors || ""}
                      </span>
                      <div style={{
                        width: "100%",
                        height: `${Math.max(pct, d.unique_visitors > 0 ? 6 : 0)}%`,
                        background: "#323233",
                        borderRadius: "2px 2px 0 0",
                      }} />
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ============== FTP Race Analysis (FormFav-powered) ==============

const AU_NZ = new Set(["au", "nz"]);
const RACE_CODES = [
  ["gallops", "Horses"],
  ["harness", "Harness"],
  ["greyhounds", "Greyhounds"],
];

// Pill button that opens a hidden file input, uploads Sportsbet results
// screenshots to the OCR/recalibrate pipeline, then polls /status until
// the job completes. Surfaces a compact toast of hit-rate when done.
const ResultsUploadPill = ({ token, date, raceCode = "gallops" }) => {
  const inputRef = useRef(null);
  const [status, setStatus] = useState(null);  // null | "uploading" | "ocr" | "scoring" | "recalibrating" | "done" | "error"
  const [result, setResult] = useState(null);  // { hits, recalibration }
  const [errMsg, setErrMsg] = useState("");
  const pollRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/formfav/upload_results/status`,
        { headers: authHeaders(token) },
      );
      setStatus(data.stage);
      if (!data.running && (data.stage === "done" || data.stage === "error")) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (data.stage === "done") {
          setResult({
            hits: data.hits, recalibration: data.recalibration,
            races_logged: data.races_logged, factor_signal: data.factor_signal,
          });
          // Auto-clear the success toast after 12s.
          setTimeout(() => { setStatus(null); setResult(null); }, 12000);
        } else {
          setErrMsg(data.error || "Upload failed");
          setTimeout(() => { setStatus(null); setErrMsg(""); }, 12000);
        }
      }
    } catch (e) {
      // 401 will be handled by the parent's onLogout via axios interceptor
      // — here we just bail out.
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [token]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    if (files.length > 12) {
      setErrMsg("Max 12 screenshots per upload");
      setTimeout(() => setErrMsg(""), 6000);
      return;
    }
    setStatus("uploading");
    setResult(null);
    setErrMsg("");
    try {
      const fd = new FormData();
      fd.append("date", date);
      fd.append("race_code", raceCode);
      for (const f of files) fd.append("files", f, f.name);
      await axios.post(`${API}/admin/formfav/upload_results`, fd, {
        headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
      });
      // Start polling.
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, 1500);
      poll();
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Upload failed");
      setTimeout(() => { setStatus(null); setErrMsg(""); }, 8000);
    }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const busy = status && status !== "done" && status !== "error" && status !== null;
  const label = (() => {
    if (errMsg) return errMsg;
    if (result) {
      const h = result.hits || {};
      const recalStatus = result.recalibration?.status;
      const recalChip = recalStatus === "ok" ? "✓ recalibrated" : recalStatus || "";
      return `${result.races_logged} races logged · ${h.top1 || 0}/${h.of || 0} top-1 · ${recalChip}`;
    }
    if (status === "uploading") return "Uploading…";
    if (status === "ocr") return "Reading results…";
    if (status === "scoring") return "Scoring runners…";
    if (status === "recalibrating") return "Tweaking weights…";
    return "Upload Results";
  })();

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        onChange={onPick}
        data-testid="results-upload-input"
        style={{ display: "none" }}
      />
      <button
        type="button"
        data-testid="results-upload-pill"
        onClick={() => inputRef.current?.click()}
        disabled={!!busy}
        title="Upload Sportsbet result screenshots — OCR'd via Gemini Vision and fed into the FTP rating model."
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "5px 12px", borderRadius: 999,
          border: "none", outline: "none", cursor: busy ? "wait" : "pointer",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
          fontFamily: "inherit",
          background: errMsg ? "#fef2f2" : (result ? "#ecfdf5" : "#323233"),
          color: errMsg ? "#b91c1c" : (result ? "#047857" : "#fff"),
          opacity: busy ? 0.85 : 1,
          maxWidth: 280, whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis",
        }}>
        {busy ? <Spinner size={12} weight="bold" className="ftp-spin" />
          : (result ? <CheckCircle size={12} weight="bold" />
                    : <Upload size={12} weight="bold" />)}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      </button>
    </>
  );
};

const FormFavPanel = ({ token, onLogout, chatCollapsed = false }) => {
  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const [date, setDate] = useState(today);
  const [region, setRegion] = useState("aunz"); // aunz | int
  const [raceCode, setRaceCode] = useState("gallops");
  const [meetings, setMeetings] = useState([]);
  const [track, setTrack] = useState("");
  const [race, setRace] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [topPicks, setTopPicks] = useState(null);
  const [valuePicks, setValuePicks] = useState(null);    // top 10 sorted by +value
  const [edgePicks, setEdgePicks] = useState(null);      // top 10 sorted by edge%
  const [smmPicks, setSmmPicks] = useState(null);        // top 10 smart money movers (opening→current shortening, sorted by confidence)ed by displayed edge
  const [rawPicks, setRawPicks] = useState(null);        // top 10 by RAW ftpScore — pre-modifier, pre-filter (v6.5)
  const [godPicks, setGodPicks] = useState(null);        // V7.5.22 — Grade of Day (A+/A on reliable races, one per race)
  const [loadingTopPicks, setLoadingTopPicks] = useState(false);
  // null = default hero view (BOD + VOD cards auto-shown).
  // 'bod' / 'vod' / 'eod' = clicking the pill expands to that category's top 10.
  // Clicking the active pill again returns to null (hero view).
  const [picksView, setPicksView] = useState(null);
  const [ppPrices, setPpPrices] = useState({});       // name(lower) -> price
  const [trackPicks, setTrackPicks] = useState(null); // one-per-race when track selected sans race

  // Re-fetch meetings + auto top-picks any time the date / race code / region changes.
  useEffect(() => {
    if (!token) return;
    setLoadingMeetings(true);
    setMeetings([]); setTrack(""); setRace(""); setData(null); setError("");
    setTopPicks(null);
    setValuePicks(null);
    setEdgePicks(null);
    setPpPrices({});
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/formfav/meetings`, {
          headers: authHeaders(token),
          params: { date, race_code: raceCode },
        });
        const all = data?.meetings || [];
        const filtered = all.filter((m) => {
          const isAuNz = AU_NZ.has(m.country);
          return region === "aunz" ? isAuNz : !isAuNz;
        }).sort((a, b) => a.track.localeCompare(b.track));
        setMeetings(filtered);
      } catch (err) {
        if (err?.response?.status === 401) onLogout();
        setError("Couldn't load meetings.");
      } finally { setLoadingMeetings(false); }
    })();
    // Parallel: top picks across the day so admin sees value before picking a track.
    // Two parallel calls — one sorted by confidence (BOD list), one by
    // positive market-vs-fair value edge (VOD list). Hero view uses
    // pick[0] from each.
    setLoadingTopPicks(true);
    (async () => {
      try {
        const [bodRes, vodRes, eodRes, smmRes, rawRes, godRes] = await Promise.all([
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "confidence" },
          }),
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "value" },
          }).catch(() => ({ data: { top_picks: [] } })),
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "edge" },
          }).catch(() => ({ data: { top_picks: [] } })),
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "smm" },
          }).catch(() => ({ data: { top_picks: [] } })),
          // RAW pill (v6.5) — pure ftpScore, no modifiers, no filters.
          // Helps the admin see whether the filter layer is eroding edge.
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "raw" },
          }).catch(() => ({ data: { top_picks: [] } })),
          // V7.5.22 — GOD pill — A+/A grades on race_reliability ≥ 70,
          // one horse per race (highest horse_score), top-20 per day.
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 20,
                       min_win_prob: 0, sort_by: "god" },
          }).catch(() => ({ data: { top_picks: [] } })),
        ]);
        const data = bodRes.data;
        const picks = data?.top_picks || [];
        setTopPicks(picks);
        setValuePicks(vodRes.data?.top_picks || []);
        setEdgePicks(eodRes.data?.top_picks || []);
        setSmmPicks(smmRes.data?.top_picks || []);
        setRawPicks(rawRes.data?.top_picks || []);
        setGodPicks(godRes.data?.top_picks || []);
        // Seed ppPrices map from inline prices (cache-warm case).
        const seed = {};
        for (const p of picks) {
          if (p.ppPrice != null) seed[(p.name || "").toLowerCase()] = p.ppPrice;
        }
        if (Object.keys(seed).length) setPpPrices((m) => ({ ...m, ...seed }));
        // If cache wasn't warm server-side, kick off a lazy /pp_prices fetch
        // so the rest of the rows pick up prices ~20s later.
        if (!data?.pp_cache_warm) {
          (async () => {
            try {
              const { data: pp } = await axios.get(`${API}/admin/formfav/pp_prices`, {
                headers: authHeaders(token),
                params: { date },
                timeout: 90000,
              });
              const map = {};
              for (const [name, info] of Object.entries(pp?.prices || {})) {
                map[name] = info?.price ?? null;
              }
              if (Object.keys(map).length) setPpPrices((m) => ({ ...m, ...map }));
            } catch { /* silent */ }
          })();
        }
      } catch (err) {
        if (err?.response?.status === 401) onLogout();
        setTopPicks([]);
        setValuePicks([]);
        setEdgePicks([]);
        setSmmPicks([]);
      } finally { setLoadingTopPicks(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, date, raceCode, region]);

  // Auto-refresh BOD + VOD picks every 45 seconds so live TAB odds
  // stay near-real-time (FormStack ingests every 90s; combined with the
  // backend's live-overlay this means displayed prices are seldom more
  // than ~2 min behind the bookies). Only polls when we're on the
  // day-wide view (no track / no race selected) and not loading.
  useEffect(() => {
    if (!token || track || race) return;
    const tick = async () => {
      try {
        const [bodRes, vodRes, eodRes, smmRes, rawRes, godRes] = await Promise.all([
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "confidence" },
          }),
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "value" },
          }).catch(() => ({ data: { top_picks: [] } })),
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "edge" },
          }).catch(() => ({ data: { top_picks: [] } })),
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "smm" },
          }).catch(() => ({ data: { top_picks: [] } })),
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 10,
                       min_win_prob: 0, sort_by: "raw" },
          }).catch(() => ({ data: { top_picks: [] } })),
          // V7.5.22 — GOD pill — A+/A grades on race_reliability ≥ 70,
          // one horse per race (highest horse_score), top-20 per day.
          axios.get(`${API}/admin/formfav/top_picks`, {
            headers: authHeaders(token),
            params: { date, race_code: raceCode, region, limit: 20,
                       min_win_prob: 0, sort_by: "god" },
          }).catch(() => ({ data: { top_picks: [] } })),
        ]);
        setTopPicks(bodRes.data?.top_picks || []);
        setValuePicks(vodRes.data?.top_picks || []);
        setEdgePicks(eodRes.data?.top_picks || []);
        setSmmPicks(smmRes.data?.top_picks || []);
        setRawPicks(rawRes.data?.top_picks || []);
        setGodPicks(godRes.data?.top_picks || []);
      } catch { /* silent — keep previous picks visible */ }
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, date, raceCode, region, track, race]);

  // When a track is selected (but no race yet) → fetch one-pick-per-race so
  // the admin sees a confidence summary for that meeting before drilling in.
  useEffect(() => {
    if (!token || !track || race) { setTrackPicks(null); return; }
    let cancelled = false;
    (async () => {
      setTrackPicks(null);
      try {
        const { data } = await axios.get(`${API}/admin/formfav/track_picks`, {
          headers: authHeaders(token),
          params: { date, track, race_code: raceCode, per_race: 1 },
        });
        if (cancelled) return;
        const picks = data?.top_picks || [];
        setTrackPicks(picks);
        const seed = {};
        for (const p of picks) {
          if (p.ppPrice != null) seed[(p.name || "").toLowerCase()] = p.ppPrice;
        }
        if (Object.keys(seed).length) setPpPrices((m) => ({ ...m, ...seed }));
      } catch {
        if (!cancelled) setTrackPicks([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, date, track, raceCode]);

  const currentMeeting = meetings.find((m) => m.slug === track);
  const availableRaces = currentMeeting?.races || [];

  // Auto-pull whenever both track + race are selected — no button needed.
  useEffect(() => {
    if (!track || !race) return;
    fetchRace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, race]);

  const fetchRace = async () => {
    if (!track || !race) return;
    setBusy(true); setError(""); setData(null);
    try {
      const { data } = await axios.get(`${API}/admin/formfav/race`, {
        headers: authHeaders(token),
        params: { date, track, race: Number(race), race_code: raceCode },
      });
      if (data?.error) setError(data.error);
      else setData(data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      setError(err?.response?.data?.detail || "Couldn't load race.");
    } finally { setBusy(false); }
  };

  const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
  const fmtOdds = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

  return (
    <div data-testid="formfav-panel"
      style={{
        marginTop: chatCollapsed ? 0 : 16,
        borderTop: chatCollapsed ? "none" : "1px solid #e5e5e4",
        background: "#fff",
        padding: "14px 16px 16px",
        // When chat is collapsed, the FormFav panel fills the entire
        // remaining viewport (chat scroll + composer are hidden). When
        // chat is expanded, it stays capped at 55dvh so the chat keeps
        // its share of the screen.
        flex: chatCollapsed ? "1 1 auto" : "0 0 auto",
        maxHeight: chatCollapsed ? "100%" : "55dvh",
        minHeight: 0,
        overflowY: "auto",
      }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: "#323233",
        textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10,
      }}>
        FTP Race Analysis
      </div>

      {/* Row 1: Date pill + Region toggle — centered */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
        <label data-testid="formfav-date-pill"
          style={{
            position: "relative", display: "inline-flex", alignItems: "center",
            gap: 6, padding: "5px 10px 5px 10px", borderRadius: 999,
            background: "#f4f4f3", color: "#323233", fontSize: 11,
            fontWeight: 700, letterSpacing: "0.02em", cursor: "pointer",
            minWidth: 130, justifyContent: "center",
          }}>
          <Calendar size={12} weight="bold" />
          <input data-testid="formfav-date" type="date" value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              padding: 0, borderRadius: 0, border: "none", outline: "none",
              boxShadow: "none", background: "transparent",
              fontSize: 11, fontFamily: "inherit",
              fontWeight: 700, letterSpacing: "0.02em",
              color: "#323233", WebkitAppearance: "none",
              cursor: "pointer", width: 96,
            }} />
          <CaretDown size={10} weight="bold" />
        </label>
        <Pills
          testid="formfav-region"
          value={region}
          options={[["aunz", "AUS/NZ"], ["int", "INT"]]}
          onChange={setRegion}
        />
      </div>

      {/* Row 2: Race code toggle */}
      <div style={{ marginBottom: 10 }}>
        <Pills
          testid="formfav-racecode"
          value={raceCode}
          options={RACE_CODES}
          onChange={setRaceCode}
          stretch
        />
      </div>

      {/* Row 3: Track + race (auto-pulls) */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <select data-testid="formfav-track" value={track}
          disabled={loadingMeetings || meetings.length === 0}
          onChange={(e) => { setTrack(e.target.value); setRace(""); setData(null); }}
          style={{
            flex: 2, padding: "7px 10px", borderRadius: 8,
            border: "1px solid #e5e5e4", fontSize: 12, background: "#fff",
            fontFamily: "inherit", color: "#323233",
          }}>
          <option value="">
            {loadingMeetings ? "Loading…" : (meetings.length === 0 ? "No meetings" : "Track…")}
          </option>
          {meetings.map((m) => (
            <option key={m.slug} value={m.slug}>
              {m.track}{m.abandoned ? " (ABD)" : ""}
            </option>
          ))}
        </select>
        <select data-testid="formfav-race" value={race}
          disabled={!currentMeeting}
          onChange={(e) => { setRace(e.target.value); }}
          style={{
            flex: 1, padding: "7px 10px", borderRadius: 8,
            border: "1px solid #e5e5e4", fontSize: 12, background: "#fff",
            fontFamily: "inherit", color: "#323233",
          }}>
          <option value="">{busy ? "Loading…" : "Race"}</option>
          {availableRaces.map((r) => (
            <option key={r.raceNumber} value={r.raceNumber}>
              R{r.raceNumber}{r.abandoned ? " (ABD)" : ""}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ color: "#b91c1c", fontSize: 12, padding: "6px 0" }}>{error}</div>
      )}

      {/* Top picks — when no track is selected. Default view shows the
          BOD + VOD hero cards. The BOD/VOD pill toggle expands either
          category to its top-8 list. Clicking the active pill returns
          to the hero view. */}
      {!data && !track && (
        <>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, gap: 6 }}>
            <button data-testid="picks-toggle-bod" type="button"
              onClick={() => setPicksView(picksView === "bod" ? null : "bod")}
              style={{
                fontFamily: "inherit", cursor: "pointer",
                padding: "5px 14px", borderRadius: 999, fontSize: 10,
                fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: picksView === "bod" ? "#15803d" : "#f4f4f3",
                color: picksView === "bod" ? "#fff" : "#525252",
                border: "none",
              }}>BOD</button>
            <button data-testid="picks-toggle-vod" type="button"
              onClick={() => setPicksView(picksView === "vod" ? null : "vod")}
              style={{
                fontFamily: "inherit", cursor: "pointer",
                padding: "5px 14px", borderRadius: 999, fontSize: 10,
                fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: picksView === "vod" ? "#b45309" : "#f4f4f3",
                color: picksView === "vod" ? "#fff" : "#525252",
                border: "none",
              }}>VOD</button>
            <button data-testid="picks-toggle-eod" type="button"
              onClick={() => setPicksView(picksView === "eod" ? null : "eod")}
              style={{
                fontFamily: "inherit", cursor: "pointer",
                padding: "5px 14px", borderRadius: 999, fontSize: 10,
                fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: picksView === "eod" ? "#0369a1" : "#f4f4f3",
                color: picksView === "eod" ? "#fff" : "#525252",
                border: "none",
              }}>EOD</button>
            <button data-testid="picks-toggle-smm" type="button"
              onClick={() => setPicksView(picksView === "smm" ? null : "smm")}
              title="Smart Money Movers — top 10 horses by sustained shortening, listed in order of confidence"
              style={{
                fontFamily: "inherit", cursor: "pointer",
                padding: "5px 14px", borderRadius: 999, fontSize: 10,
                fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: picksView === "smm" ? "#15803d" : "#f4f4f3",
                color: picksView === "smm" ? "#fff" : "#525252",
                border: "none",
              }}>SMM</button>
            {/* RAW pill (v6.5) — pre-modifier, pre-filter ranking. Pure
                `ftpScore`. Sits last in the pill row because it's the
                diagnostic/comparison view: lets the admin see what the
                rating engine "really" thinks before the filter layer
                adjusts ordering. Indigo theme to visually distinguish
                from the four scored pills. */}
            <button data-testid="picks-toggle-raw" type="button"
              onClick={() => setPicksView(picksView === "raw" ? null : "raw")}
              title="Raw FTP scores — top 10 horses by pure ftpScore, before any modifiers or filters. Diagnostic view to compare against BOD."
              style={{
                fontFamily: "inherit", cursor: "pointer",
                padding: "5px 14px", borderRadius: 999, fontSize: 10,
                fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: picksView === "raw" ? "#4f46e5" : "#f4f4f3",
                color: picksView === "raw" ? "#fff" : "#525252",
                border: "none",
              }}>RAW</button>
            {/* GOD pill (V7.5.22) — Grade of Day.  Filters for A+/A
                quality grades on highly-reliable races (race_reliability
                ≥ 70), one horse per race only, sorted by horse_score
                DESC then race_reliability DESC.  Top-20 per day.
                Emerald theme to distinguish from the four price-based
                lists. */}
            <button data-testid="picks-toggle-god" type="button"
              onClick={() => setPicksView(picksView === "god" ? null : "god")}
              title="Grade of Day — A+/A graded horses on races with reliability ≥ 70. One horse per race. Top 20."
              style={{
                fontFamily: "inherit", cursor: "pointer",
                padding: "5px 14px", borderRadius: 999, fontSize: 10,
                fontWeight: 800, letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: picksView === "god" ? "#16a34a" : "#f4f4f3",
                color: picksView === "god" ? "#fff" : "#525252",
                border: "none",
              }}>GOD</button>
          </div>
          {picksView === null ? (
            <HeroBodVod
              bod={(topPicks || []).find((p) => p.isBestOfDay) || (topPicks || [])[0] || null}
              vod={(valuePicks || []).find((p) => p.isValueOfDay)
                   || (valuePicks || [])[0]
                   || (topPicks || []).find((p) => p.isValueOfDay)
                   || null}
              eod={(edgePicks || [])[0] || null}
              loading={loadingTopPicks}
              fmtPct={fmtPct}
              fmtOdds={fmtOdds}
              onPick={(p) => { setTrack(p.slug); setRace(String(p.race)); }}
              token={token}
              onLogout={onLogout}
              date={date}
              raceCode={raceCode}
              ppPrices={ppPrices}
            />
          ) : (
            <TopPicksList
              picks={picksView === "vod" ? valuePicks
                   : picksView === "eod" ? edgePicks
                   : picksView === "smm" ? smmPicks
                   : picksView === "raw" ? rawPicks
                   : picksView === "god" ? godPicks
                   : topPicks}
              loading={loadingTopPicks}
              fmtPct={fmtPct}
              fmtOdds={fmtOdds}
              onPick={(p) => { setTrack(p.slug); setRace(String(p.race)); }}
              token={token}
              onLogout={onLogout}
              date={date}
              raceCode={raceCode}
              ppPrices={ppPrices}
              viewMode={picksView /* null | bod | vod | eod | smm | raw | god */}
              bodPicks={topPicks}
              vodPicks={valuePicks}
              edgePicks={edgePicks}
              smmPicks={smmPicks}
              rawPicks={rawPicks}
              godPicks={godPicks}
              heading={picksView === "vod" ? "Today's Top Value Plays"
                     : picksView === "eod" ? "Today's Top Edge Plays"
                     : picksView === "smm" ? "Today's Smart Money Movers"
                     : picksView === "raw" ? "Today's Top RAW FTP Scores (pre-modifier)"
                     : picksView === "god" ? "Today's Top Graded Plays (A+ / A)"
                     : "Today's Top Confidence Bets"}
            />
          )}
        </>
      )}

      {/* When a track is picked (but not a race), show one top pick per race. */}
      {!data && track && !race && (
        <TopPicksList
          picks={trackPicks}
          loading={trackPicks === null}
          fmtPct={fmtPct}
          fmtOdds={fmtOdds}
          onPick={(p) => { setRace(String(p.race)); }}
          token={token}
          onLogout={onLogout}
          date={date}
          raceCode={raceCode}
          ppPrices={ppPrices}
          heading={`${currentMeeting?.track || track} — Top Pick Per Race`}
        />
      )}

      {data && <FormFavResult data={data} fmtPct={fmtPct} fmtOdds={fmtOdds}
        token={token} onLogout={onLogout}
        date={date} raceCode={raceCode} ppPrices={ppPrices}
        trackSlug={track} raceNumber={Number(race)} />}

      {/* End-of-Day Report — pulled from /admin/daily_report. Renders
          empty when no settled picks exist yet, fills in live during
          the day as FormStack grades races, and finalises after the
          22:30 Melbourne recalibration cron with weight deltas. */}
      <DailyReportBlock token={token} onLogout={onLogout} />
    </div>
  );
};


// V7.0 (Feb 2026) — System Health strip.  Surfaces FormFav daily
// budget telemetry + pillar anomaly download button.  Sits above the
// Backtest/Ablation tile inside the DailyReportBlock.
const SystemHealthStrip = ({ token }) => {
  const [usage, setUsage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  // V7.2 Session 15 — short-lived "Copied!" confirmation state for
  // the "Copy today's AI Assist URL" button.  Auto-clears after
  // 1.8 s so the button returns to its default label without
  // requiring user interaction.
  const [urlCopied, setUrlCopied] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [availDates, setAvailDates] = useState([]);

  const loadAvailDates = (month) => {
    const mk = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
    fetch(`${BACKEND_URL}/api/ai_assist/available_dates?month=${mk}`)
      .then((r) => r.json())
      .then((d) => setAvailDates((d.dates || []).map((x) => new Date(`${x}T00:00:00`))))
      .catch(() => {});
  };

  useEffect(() => {
    let alive = true;
    // V7.4.5 — each fetch is INDEPENDENT.  This strip previously used
    // a single Promise.all: when the daily-top4-summary endpoint hung
    // (FormFav rate-limited → ingress 502 after ~60s) the api-usage
    // result never landed, `usage` stayed null and the WHOLE strip —
    // anomalies button, AI Assist URL + download buttons — vanished.
    const fetchAll = () => {
      fetch(`${BACKEND_URL}/api/admin/system/api-usage`,
        { headers: authHeaders(token) })
        .then((r) => r.json())
        .then((d) => { if (alive) setUsage(d); })
        .catch(() => { /* swallow — strip is non-critical */ });
      fetch(`${BACKEND_URL}/api/admin/logs/anomalies/summary`,
        { headers: authHeaders(token) })
        .then((r) => r.json())
        .then((d) => { if (alive) setSummary(d); })
        .catch(() => { /* swallow */ });
      fetch(`${BACKEND_URL}/api/admin/diagnostics/daily-top4-summary`,
        { headers: authHeaders(token) })
        .then((r) => r.json())
        .then((d) => { if (alive) setAiSummary(d); })
        .catch(() => { /* swallow */ });
    };
    fetchAll();
    const t = setInterval(fetchAll, 60000); // refresh every minute
    return () => { alive = false; clearInterval(t); };
  }, [token]);

  const downloadAnomalies = async () => {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/logs/anomalies/today`,
        { headers: authHeaders(token) },
      );
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pillar_anomalies_${(summary || {}).date || "today"}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* swallow */ }
  };

  const downloadAiAssist = async (dateIso) => {
    try {
      // V7.1.1 — use the `today` alias so the backend resolves the
      // correct Melbourne ISO date (UTC slicing was producing empty
      // files for users browsing late in the AU evening).
      const r = await fetch(
        `${BACKEND_URL}/api/admin/diagnostics/daily-top4-export/${dateIso || "today"}`,
        { headers: authHeaders(token) },
      );
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = dateIso || (aiSummary && aiSummary.date)
        || new Date().toISOString().slice(0, 10);
      a.download = `ftp_ai_assist_${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* swallow */ }
  };

  // V7.2 Session 15 — copy handler for the "Copy today's AI Assist
  // URL" button.  Uses ``window.location.origin`` so the URL
  // automatically resolves to whichever host is rendering the
  // dashboard (preview vs ftpformedge.com.au), without requiring
  // a hard-coded production string.
  const copyAiAssistUrl = async () => {
    const url = `${window.location.origin}/api/ai_assist`;
    try {
      await navigator.clipboard.writeText(url);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 1800);
    } catch (e) {
      // Fallback for older browsers / non-HTTPS contexts.
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_) { /* swallow */ }
      ta.remove();
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 1800);
    }
  };

  if (!usage) return null;
  const status = usage.status || "active";
  const statusColour = status === "rate_limited" ? "#dc2626"
    : status === "warning" ? "#d97706" : "#16a34a";
  const pct = Number(usage.budgetUsedPct || 0);
  const anomalyCount = (summary && summary.total) || 0;
  const hasAnomalies = anomalyCount > 0;

  // Shared grey button palette so all action buttons match the site
  // aesthetic (the FormFav-usage tile keeps its semantic green dot
  // + green bar).  Disabled state lightens further.
  // V7.2 Session 16 — text centred per Harry's request so every
  // button reads symmetrically regardless of its label length.
  const greyBtn = (disabled = false) => ({
    padding: "10px 14px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    border: "1px solid #d4d4d8",
    background: disabled ? "#fafafa" : "#f4f4f5",
    color: disabled ? "#a1a1aa" : "#27272a",
    cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
    width: "100%",
    textAlign: "center",
    transition: "background 120ms ease",
  });

  // V7.2 Session 16 — red-tinted variant for the Anomalies button
  // when ``hasAnomalies`` is true.  Keeps the same shape as the
  // grey style so the button stays visually a peer of the others,
  // but the red border + text turn the button itself into the
  // warning signal (replaces the small red chip we used to show
  // alongside it).
  const redBtn = {
    padding: "10px 14px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    border: "1px solid #fca5a5",
    background: "#fef2f2",
    color: "#b91c1c",
    cursor: "pointer",
    whiteSpace: "nowrap",
    width: "100%",
    textAlign: "center",
    transition: "background 120ms ease",
  };

  return (
    <div
      data-testid="system-health-strip"
      style={{
        marginTop: 14,
        padding: "12px 14px",
        background: "#fff",
        borderRadius: 6,
        fontSize: 11,
        color: "#3f3f46",
        border: "1px solid #e5e5e5",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: 12,
        justifyContent: "space-between",
      }}
    >
      {/* Left — FormFav usage tile.  V7.2 Session 15: the
          "SYSTEM HEALTH" label has been removed per request; the
          green dot + green bar already convey the health signal
          unambiguously and the extra uppercase caption was adding
          visual noise on mobile where the strip wraps to two
          rows. */}
      <div
        data-testid="formfav-budget-tile"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          flex: "1 1 280px",
          minWidth: 0,
          padding: "4px 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8,
          flexWrap: "wrap" }}>
          <span style={{ display: "inline-block", width: 8, height: 8,
            borderRadius: "50%", background: statusColour,
            flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 700,
            color: "#18181b" }}>
            FormFav&nbsp;
            <span style={{ fontWeight: 600, color: "#3f3f46" }}>
              {usage.used?.toLocaleString?.() || 0}
              {" / "}
              {usage.limit?.toLocaleString?.() || 0}
            </span>
          </span>
          <span style={{ fontSize: 10, color: "#71717a" }}>
            ({pct.toFixed(1)}% · {usage.remaining?.toLocaleString?.() || 0} left)
          </span>
          {usage.source === "formfav_headers" && (
            <span style={{ fontSize: 9, color: "#a1a1aa",
              fontStyle: "italic" }}>live</span>
          )}
          {usage.circuitBreakerOpen && (
            <span data-testid="circuit-breaker-open" style={{
              fontSize: 10, color: "#dc2626", fontWeight: 700,
            }}>
              ⚠ breaker open
            </span>
          )}
        </div>
        {/* Mini bar — stays green to keep the semantic signal */}
        <div style={{ width: "100%", maxWidth: 260, height: 6,
          background: "#f3f4f6", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${Math.min(100, pct)}%`,
            height: "100%", background: statusColour }} />
        </div>
      </div>

      {/* Right — action buttons stack.  V7.2 Session 15:
          - Replaced the long URL banner with a single "Copy
            today's AI Assist URL" button (greyed) that copies
            the live origin + ``/api/ai_assist`` to the clipboard
            and flips to "URL copied!" for ~2 s as confirmation.
          - All buttons now share the grey palette (was blue / red).
          - Pillar Anomalies button keeps a small red count chip
            on the right when anomalies are present (the only
            non-grey accent) so the warning still registers
            at a glance.
          - Stack is ``flex-direction: column`` with
            ``align-items: stretch`` so on mobile each row gets
            full width; on desktop the column sits to the right
            of the FormFav tile via the parent ``flex-wrap``. */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        flex: "1 1 280px",
        minWidth: 0,
        alignItems: "stretch",
      }}>
        {/* Copy URL button */}
        <button
          data-testid="copy-ai-assist-url-btn"
          onClick={copyAiAssistUrl}
          style={greyBtn(false)}
          title={`${typeof window !== "undefined" ? window.location.origin : "https://ftpformedge.com.au"}/api/ai_assist`}
        >
          {urlCopied ? "✓ URL copied!" : "Copy today's AI Assist URL"}
        </button>

        {/* Download AI Assist — full-width, centred.  V7.2 Session
            17: the date caption beneath this button has been
            removed too — the strip now reads as three peer
            actions with no trailing metadata. */}
        <button
          data-testid="download-ai-assist-btn"
          onClick={() => downloadAiAssist()}
          style={greyBtn(false)}
        >
          Download Today's AI Assist
        </button>

        <Popover open={calOpen} onOpenChange={(o) => { setCalOpen(o); if (o) loadAvailDates(new Date()); }}>
          <PopoverTrigger asChild>
            <button data-testid="download-ai-assist-datepicker-btn" style={greyBtn(false)}>
              <Calendar size={16} /> Download by Date
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <DatePickerCalendar
              mode="single"
              onMonthChange={loadAvailDates}
              modifiers={{ available: availDates }}
              modifiersClassNames={{ available: "after:content-[''] after:block after:w-1 after:h-1 after:bg-black after:rounded-full after:mx-auto after:mt-0.5" }}
              onSelect={(d) => {
                if (!d) return;
                const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                downloadAiAssist(iso);
                setCalOpen(false);
              }}
              data-testid="download-ai-assist-calendar"
            />
          </PopoverContent>
        </Popover>

        {/* Download Anomalies — full-width, centred.  V7.2 Session 16:
            replaces the previous grey-button-plus-red-count-chip
            pattern with a single button that turns red when any
            anomalies are present (so the warning lives on the
            button itself, not in an adjacent chip).  Disabled +
            grey when there's nothing to download. */}
        <button
          data-testid="download-anomalies-btn"
          onClick={downloadAnomalies}
          disabled={!hasAnomalies}
          style={hasAnomalies ? redBtn : greyBtn(true)}
        >
          {hasAnomalies
            ? "Download Today's Pillar Anomalies"
            : "No Anomalies Detected"}
        </button>

        {/* V7.5.16 (2026-06-27) — Admin results workbook.  Multi-tab
            XLSX with BOD/VOD/EOD/RAW (last 3 weeks) plus a Master
            Data tab covering every selection ever made.  Updated
            nightly by the same cron that builds the AI Assist
            export. */}
        <button
          data-testid="download-results-btn"
          onClick={async () => {
            try {
              const res = await axios.get(
                `${BACKEND_URL}/api/admin/results/export.xlsx`,
                { headers: authHeaders(token), responseType: "blob" },
              );
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a");
              a.href = url;
              a.download = `ftp_results_${new Date()
                .toISOString().slice(0, 10)}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) {
              alert("Results export failed — check the backend logs.");
              console.error("results export error:", e);
            }
          }}
          style={greyBtn()}
        >
          Download FTP Results
        </button>
      </div>
    </div>
  );
};


// Compact end-of-day summary: today's official picks + finish positions
// + ROI + any model weight nudges from the 22:30 recalibration.
const DailyReportBlock = ({ token, onLogout }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  // Active ablation window (v6.12). User can flip between
  // 14d (display headline), 28d (what the auto-tuner acts on),
  // 90d (strategic), and lifetime/since-live.
  const [ablationWindow, setAblationWindow] = useState("14d");
  // V7.2 — manual "Run Backtest Now" button.  Triggers the full
  // nightly recalibration (results ingest → learning loop →
  // ablation → weight delta capture) on demand and refreshes the
  // tile when it finishes.  Used when the 21:15 cron was missed.
  const [runningBacktest, setRunningBacktest] = useState(false);
  const [backtestMsg, setBacktestMsg] = useState(null);
  const refreshReport = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/daily_report`, {
        headers: authHeaders(token),
      });
      setReport(data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  };
  const runBacktestNow = async () => {
    if (runningBacktest) return;
    if (!window.confirm(
      "Run the backtest + ablation now? Picks up everything that's "
      + "accumulated since the last successful run (results ingest, "
      + "learning loop, ablation windows, weight deltas). Usually "
      + "takes 2–5 minutes."
    )) return;
    setRunningBacktest(true);
    setBacktestMsg("Starting…");
    try {
      const { data } = await axios.post(
        `${API}/admin/recalibration/run_now`, {},
        { headers: authHeaders(token) },
      );
      if (data?.status === "already_running") {
        setBacktestMsg("Already running — polling for completion.");
      } else if (data?.status === "started") {
        setBacktestMsg("Running… this can take a few minutes.");
      } else {
        setBacktestMsg(`Unexpected response: ${JSON.stringify(data)}`);
        setRunningBacktest(false);
        return;
      }
      // Poll status until completion.  Backend exposes a GET twin
      // returning {running, last_error, last_run, finished_at}.
      const pollOnce = async () => {
        const { data: st } = await axios.get(
          `${API}/admin/recalibration/run_now`,
          { headers: authHeaders(token) },
        );
        if (st.running) {
          setBacktestMsg("Running… this can take a few minutes.");
          return false;
        }
        if (st.last_error) {
          setBacktestMsg(`Failed: ${st.last_error}`);
        } else {
          setBacktestMsg("Done — stats refreshed.");
          await refreshReport();
        }
        return true;
      };
      const start = Date.now();
      while (Date.now() - start < 10 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 10000));  // 10s
        try {
          const done = await pollOnce();
          if (done) break;
        } catch (e) {
          // transient — keep polling
        }
      }
    } catch (e) {
      setBacktestMsg(`Failed to start: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunningBacktest(false);
      setTimeout(() => setBacktestMsg(null), 10000);
    }
  };
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const tick = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/daily_report`, {
          headers: authHeaders(token),
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (err?.response?.status === 401) onLogout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tick();
    const id = setInterval(tick, 5 * 60 * 1000);  // refresh every 5 min
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading && !report) return null;
  if (!report || (report.selections_count === 0 && !report.picks?.length)) {
    return null;
  }

  const dateLabel = (() => {
    const ds = report.date_string || "";
    const m = ds.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return ds;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${parseInt(m[1], 10)}-${months[parseInt(m[2], 10) - 1]}-${m[3].slice(2)}`;
  })();
  // Once the 22:30 cron has populated `full_field_stats`, the panel
  // pivots from "Top 15 results" to "Full-card review" — i.e. the
  // model's #1 pick performance across EVERY race we scored today.
  // This is the proper backtest summary the user requested.
  const ff = report.full_field_stats;
  const useFullField = !!(ff && ff.races_with_results);
  const statsRoi = useFullField ? ff.top1_roi_pct : report.roi_pct;
  const roiColour = statsRoi > 0 ? "#0a7d3a" : statsRoi < 0 ? "#b91c1c" : "#888";
  return (
    <div data-testid="daily-report-block" style={{
      marginTop: 28, paddingTop: 16, borderTop: "1px solid #f0f0ef",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 10, gap: 12, flexWrap: "wrap",
      }}>
        <div style={{
          fontSize: 11, fontWeight: 800, color: "#323233",
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          {dateLabel} Results
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {backtestMsg && (
            <span data-testid="run-backtest-status" style={{
              fontSize: 10, color: runningBacktest ? "#525252"
                : (backtestMsg.startsWith("Failed") ? "#b91c1c" : "#0a7d3a"),
              fontStyle: "italic",
            }}>
              {backtestMsg}
            </span>
          )}
          <button
            data-testid="run-backtest-btn"
            onClick={runBacktestNow}
            disabled={runningBacktest}
            title="Re-runs results ingest, learning loop, ablation windows and weight deltas for today."
            style={{
              padding: "6px 12px", fontSize: 11, fontWeight: 700,
              borderRadius: 4, border: "1px solid #18181b",
              background: runningBacktest ? "#a1a1aa" : "#18181b",
              color: "#fff",
              letterSpacing: "0.05em", textTransform: "uppercase",
              cursor: runningBacktest ? "wait" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {runningBacktest ? "Running…" : "Run Backtest & Ablation"}
          </button>
        </div>
      </div>

      {/* Topline stats — once full-field data exists, shows the model's
          top-pick performance across every race we scored today. Before
          22:30 (no full-field data yet) falls back to the top-15 view. */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
        gap: 8, marginBottom: 6,
      }}>
        {useFullField ? (
          <>
            <Stat
              label="Races"
              value={`${ff.races_with_results}/${ff.races_scored}`}
              sublabel="settled" />
            <Stat
              label="Top Pick Wins"
              value={ff.top1_winners}
              colour="#15803d" />
            <Stat
              label="Strike"
              value={`${(ff.top1_strike_pct ?? 0).toFixed(0)}%`} />
            <Stat
              label="ROI"
              value={`${statsRoi > 0 ? "+" : ""}${(statsRoi ?? 0).toFixed(0)}%`}
              colour={roiColour} />
          </>
        ) : (
          <>
            <Stat label="Top 15"
                  value={`${report.settled_count}/${report.selections_count}`}
                  sublabel="settled" />
            <Stat label="Winners" value={report.winners} colour="#15803d" />
            <Stat label="Strike"
                  value={`${(report.strike_rate_pct ?? 0).toFixed(0)}%`} />
            <Stat label="ROI"
                  value={`${statsRoi > 0 ? "+" : ""}${(statsRoi ?? 0).toFixed(0)}%`}
                  colour={roiColour} />
          </>
        )}
      </div>

      {/* Recalibration paragraph — appears once the 22:30 cron has
          finished. Natural-language summary describing the day's
          result, the 14-day ablation verdict, and any weight nudges.
          Hidden entirely when recal hasn't run yet today. */}
      {report.recal_paragraph && (
        <div data-testid="daily-report-recal-paragraph" style={{
          marginTop: 14, padding: "10px 12px", background: "#fafafa",
          borderRadius: 4, fontSize: 11, color: "#525252",
          lineHeight: 1.5, fontStyle: "italic",
        }}>
          {report.recal_paragraph}
        </div>
      )}
      {/* V7.0 System Health strip (Feb 2026) — sits above the
          Backtest/Ablation tile.  Surfaces the FormFav daily-budget
          status (from upstream X-RateLimit-* headers) and an admin
          download button for today's pillar anomaly log. */}
      <SystemHealthStrip token={token} />
      {/* Ablation backtest block (v6.12) — selectable rolling window:
          14d (display), 28d (what auto-tuner acts on), 90d (strategic
          medium-term), lifetime (since-live overall scorecard). Each
          window re-ranks every locked race under four variants
          (RAW / RAW+Prox / RAW+Prox+MConf / FULL) and scores them on
          Strike / ROI / CLV / Brier / EoM. */}
      {(() => {
        const WINDOW_LABELS = {
          "14d": { label: "14d", caption: "display headline", key: "ablation_summary" },
          "28d": { label: "28d", caption: "auto-tune signal", key: "ablation_summary_28d" },
          "90d": { label: "90d", caption: "strategic trend", key: "ablation_summary_90d" },
          "lifetime": { label: "Lifetime", caption: "since-live", key: "ablation_summary_lifetime" },
        };
        const active = WINDOW_LABELS[ablationWindow] || WINDOW_LABELS["14d"];
        const summary = report[active.key];
        // Don't render the whole block if no window has data.
        const anyWindowHasData = Object.values(WINDOW_LABELS).some(
          (w) => (report[w.key] || {}).races_evaluated > 0,
        );
        if (!anyWindowHasData) return null;
        return (
        <div data-testid="daily-report-ablation-block" style={{
          marginTop: 14, padding: "12px 14px", background: "#fafafa",
          borderRadius: 4, fontSize: 11, color: "#3f3f46",
          border: "1px solid #e5e5e5",
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 8, marginBottom: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{
                fontSize: 9, fontWeight: 800, color: "#525252",
                letterSpacing: "0.08em", textTransform: "uppercase",
              }}>Ablation · {active.label}</span>
              <span style={{ fontSize: 9, color: "#888" }}>
                · {summary?.races_evaluated || 0} graded races · {active.caption}
              </span>
            </div>
            {/* Window selector — flush against the right. Disabled
                tabs (no data yet) stay visible but greyed out so the
                admin sees what's coming. */}
            <div style={{ display: "flex", gap: 4 }} data-testid="ablation-window-tabs">
              {Object.entries(WINDOW_LABELS).map(([key, w]) => {
                const has = (report[w.key] || {}).races_evaluated > 0;
                const isActive = key === ablationWindow;
                return (
                  <button
                    key={key}
                    data-testid={`ablation-window-${key}`}
                    disabled={!has}
                    onClick={() => has && setAblationWindow(key)}
                    style={{
                      fontSize: 9, padding: "3px 8px", borderRadius: 3,
                      border: isActive ? "1px solid #3f3f46" : "1px solid #d4d4d4",
                      background: isActive ? "#3f3f46" : (has ? "#fff" : "#f4f4f5"),
                      color: isActive ? "#fff" : (has ? "#3f3f46" : "#a3a3a3"),
                      cursor: has ? "pointer" : "not-allowed",
                      fontWeight: isActive ? 700 : 500,
                      letterSpacing: "0.04em", textTransform: "uppercase",
                    }}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>
          {summary && summary.races_evaluated > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{
                width: "100%", borderCollapse: "collapse", fontSize: 10,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
              }}>
                <thead>
                  <tr style={{ background: "#f4f4f5" }}>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>Variant</th>
                    <th style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>Strike</th>
                    <th style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>ROI</th>
                    <th style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>CLV</th>
                    <th style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>Brier</th>
                    <th style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>EoM</th>
                  </tr>
                </thead>
                <tbody>
                  {["raw", "raw_proximity", "raw_prox_mconf", "raw_prox_mconf_depth", "full"].map((v) => {
                    const s = summary.variants?.[v];
                    if (!s) return null;
                    const labels = {
                      raw: "RAW",
                      raw_proximity: "RAW + Prox",
                      raw_prox_mconf: "RAW + Prox + MConf",
                      raw_prox_mconf_depth: "+ Field Depth",
                      full: "FULL (current)",
                    };
                    return (
                      <tr key={v} style={{ borderTop: "1px solid #e5e5e5" }}>
                        <td style={{ padding: "4px 6px", fontWeight: v === "full" ? 800 : 500 }}>
                          {labels[v]}
                        </td>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>
                          {s.strike_rate_pct}%
                        </td>
                        <td style={{
                          padding: "4px 6px", textAlign: "right",
                          color: s.roi_pct >= 0 ? "#15803d" : "#b91c1c",
                          fontWeight: 700,
                        }}>
                          {s.roi_pct >= 0 ? "+" : ""}{s.roi_pct}%
                        </td>
                        <td style={{
                          padding: "4px 6px", textAlign: "right",
                          color: s.clv_avg_pct >= 0 ? "#15803d" : "#b91c1c",
                        }}>
                          {s.clv_avg_pct >= 0 ? "+" : ""}{s.clv_avg_pct}%
                        </td>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>
                          {s.brier}
                        </td>
                        <td style={{
                          padding: "4px 6px", textAlign: "right",
                          color: s.eom_per_pick_pp >= 0 ? "#15803d" : "#b91c1c",
                        }}>
                          {s.eom_per_pick_pp >= 0 ? "+" : ""}{s.eom_per_pick_pp}pp
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{
              padding: "10px 8px", fontSize: 11, color: "#888",
              fontStyle: "italic", textAlign: "center",
            }}>
              No graded races in this window yet — try a longer one.
            </div>
          )}
          {/* Recommendation is computed off the 14d view (display
              window). Only render when looking at 14d. */}
          {ablationWindow === "14d" && report.ablation_recommendation && (
            <div data-testid="daily-report-ablation-recommendation" style={{
              marginTop: 10, padding: "8px 10px", background: "#fff",
              borderRadius: 4, fontSize: 11, color: "#3f3f46",
              lineHeight: 1.45, fontStyle: "italic",
              borderLeft: "3px solid #a1a1aa",
            }}>
              {report.ablation_recommendation}
            </div>
          )}
          {/* AUTO-TUNE BLOCK — driven off the 28d window now (v6.12).
              Only render when looking at the 28d tab so the admin sees
              the source of each tune action. */}
          {ablationWindow === "28d" && report.ablation_auto_tune && (report.ablation_auto_tune.changes || []).length > 0 && (
            <div data-testid="daily-report-auto-tune-block" style={{
              marginTop: 8, padding: "8px 10px", background: "#fff",
              borderRadius: 4, fontSize: 10, color: "#3f3f46",
              borderLeft: "3px solid #525252",
            }}>
              <span style={{ fontWeight: 800, color: "#3f3f46", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 9 }}>
                Auto-tune applied (from 28d window)
              </span>
              <ul style={{ margin: "4px 0 0 0", padding: 0, listStyle: "none" }}>
                {report.ablation_auto_tune.changes.map((c, i) => (
                  <li key={i} style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, marginTop: 2 }}>
                    {c.knob}: {c.before} → <b>{c.after}</b> ({c.direction})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
};

const Stat = ({ label, value, colour, sublabel }) => (
  <div style={{
    background: "#fafafa", padding: "8px 10px", borderRadius: 4,
    display: "flex", flexDirection: "column", gap: 2,
  }}>
    <div style={{ fontSize: 8, fontWeight: 700, color: "#888",
                   letterSpacing: "0.08em", textTransform: "uppercase" }}>
      {label}
    </div>
    <div style={{ fontSize: 16, fontWeight: 800, color: colour || "#323233" }}>
      {value}
    </div>
    {sublabel && (
      <div style={{ fontSize: 8, color: "#aaa", textTransform: "uppercase",
                     letterSpacing: "0.06em" }}>
        {sublabel}
      </div>
    )}
  </div>
);

// Default top-of-panel view: just the BOD card + VOD card. Saves the
// admin from scrolling through 8 picks when 90% of the time they only
// act on the headline plays.
const HeroBodVod = ({ bod, vod, eod, loading, fmtPct, fmtOdds, onPick, token, onLogout, date, raceCode, ppPrices = {} }) => {
  if (loading) {
    return (
      <div data-testid="hero-bodvod-loading"
        style={{ fontSize: 11, color: "#888", padding: "12px 0", textAlign: "center" }}>
        Scanning the card....
      </div>
    );
  }
  if (!bod && !vod && !eod) {
    return (
      <div data-testid="hero-bodvod-empty"
        style={{ fontSize: 11, color: "#888", padding: "12px 0", textAlign: "center", lineHeight: 1.4 }}>
        No headline picks on this card yet.
      </div>
    );
  }
  const bodCard = bod ? { ...bod, isBestOfDay: true } : null;
  // Only show VOD when it's a DIFFERENT horse from BOD.
  const vodCard = vod && (!bod || vod.number !== bod.number || vod.slug !== bod.slug || vod.race !== bod.race)
    ? { ...vod, isValueOfDay: true }
    : null;
  // EOD = top edge pick. Hide it if it duplicates BOD or VOD.
  const eodDuplicatesBod = bodCard && eod
    && eod.number === bodCard.number && eod.slug === bodCard.slug
    && eod.race === bodCard.race;
  const eodDuplicatesVod = vodCard && eod
    && eod.number === vodCard.number && eod.slug === vodCard.slug
    && eod.race === vodCard.race;
  const eodCard = eod && !eodDuplicatesBod && !eodDuplicatesVod
    ? { ...eod, isEdgeOfDay: true }
    : null;
  return (
    <div data-testid="hero-bodvod" style={{ marginTop: 4 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: "#888",
        letterSpacing: "0.08em", textTransform: "uppercase",
        marginBottom: 6,
      }}>
        Today's Headline Plays
      </div>
      {bodCard && (
        <TopPickRow p={bodCard} rank={1} fmtPct={fmtPct} fmtOdds={fmtOdds}
          onClick={() => onPick(bodCard)}
          token={token} onLogout={onLogout}
          date={date} raceCode={raceCode}
          ppPrice={ppPrices?.[(bodCard.name || "").toLowerCase()] || null}
        />
      )}
      {vodCard && (
        <TopPickRow p={vodCard} rank={2} fmtPct={fmtPct} fmtOdds={fmtOdds}
          onClick={() => onPick(vodCard)}
          token={token} onLogout={onLogout}
          date={date} raceCode={raceCode}
          ppPrice={ppPrices?.[(vodCard.name || "").toLowerCase()] || null}
        />
      )}
      {eodCard && (
        <TopPickRow p={eodCard} rank={3} fmtPct={fmtPct} fmtOdds={fmtOdds}
          onClick={() => onPick(eodCard)}
          token={token} onLogout={onLogout}
          date={date} raceCode={raceCode}
          ppPrice={ppPrices?.[(eodCard.name || "").toLowerCase()] || null}
        />
      )}
      {bodCard && !vodCard && !eodCard && (
        <div data-testid="hero-bodvod-no-value"
          style={{ fontSize: 10, color: "#aaa", padding: "8px 4px",
                   textAlign: "center", letterSpacing: "0.02em" }}>
          No qualifying value or edge play on the card today.
        </div>
      )}
    </div>
  );
};


// Compact list of the day's highest-confidence model picks.



const TopPicksList = ({ picks, loading, fmtPct, fmtOdds, onPick, token, onLogout, date, raceCode, onPromoted, ppPrices = {}, heading = "Top FTP Picks Today", viewMode = null, bodPicks = null, vodPicks = null, edgePicks = null, smmPicks = null, rawPicks = null, godPicks = null }) => {
  if (loading) {
    return (
      <div data-testid="formfav-top-picks-loading"
        style={{ fontSize: 11, color: "#888", padding: "12px 0", textAlign: "center" }}>
        Scanning the card....
      </div>
    );
  }
  if (!picks) return null;
  // v7.5.8 cross-list badge overlay + scratched/abandoned filter.
  //
  // 1) Filter out runners that have been scratched, are in abandoned
  //    races, or carry no live odds (race already done with no
  //    finishing data) — these polluted the lists yesterday.
  // 2) For each remaining pick, cross-reference BOD/VOD/EOD pick
  //    lists by (slug, race, number).  ANY pick that appears in
  //    a list carries that badge into every other list view — so a
  //    horse that's both BOD and VOD shows both pills regardless of
  //    which list is currently selected.  (Previous behaviour only
  //    overlaid the HEAD of each list, which silently dropped
  //    badges off ranks 2-20.)
  const _keySet = (arr, filterFlag) => {
    const out = new Set();
    for (const p of (arr || [])) {
      if (!p) continue;
      // Honour the server's own qualifier flag when present so we
      // don't promote rank-N runners that didn't actually pass the
      // list's filter band.
      if (filterFlag && !p[filterFlag]) continue;
      out.add(`${p.slug}-${p.race}-${p.number}`);
    }
    return out;
  };
  const bodKeys = _keySet(bodPicks, "isBestOfDay");
  const vodKeys = _keySet(vodPicks, "isValueOfDay");
  const eodKeys = _keySet(edgePicks, "isEdgeOfDay");
  // V7.5.22 — GOD membership badge.  Same multi-pill behaviour as
  // BOD/VOD/EOD so a horse that's both BOD + GOD shows both.
  const godKeys = _keySet(godPicks, "isGradeOfDay");
  // SMM + RAW headline keys remain head-only — they're "leader"
  // badges, not multi-membership pills.
  const smmKey = (() => {
    const head = (smmPicks || [])[0];
    if (!head) return null;
    return `${head.slug}-${head.race}-${head.number}`;
  })();
  const rawKey = (() => {
    const head = (rawPicks || [])[0];
    if (!head) return null;
    return `${head.slug}-${head.race}-${head.number}`;
  })();
  const decorated = (picks || []).filter((p) => {
    if (!p) return false;
    if (p.scratched || p.isScratched) return false;
    if (p.race_abandoned || p.abandoned) return false;
    return true;
  }).map((p) => {
    const key = `${p.slug}-${p.race}-${p.number}`;
    return {
      ...p,
      isBestOfDay: p.isBestOfDay || bodKeys.has(key),
      isValueOfDay: p.isValueOfDay || vodKeys.has(key),
      isEdgeOfDay: p.isEdgeOfDay || eodKeys.has(key),
      isGradeOfDay: p.isGradeOfDay || godKeys.has(key),
      isSmartMoneyMover: p.isSmartMoneyMover || key === smmKey,
      isRawLeader: p.isRawLeader || key === rawKey,
    };
  });
  if (decorated.length === 0) {
    const codeIsGallops = (raceCode || "gallops") === "gallops";
    return (
      <div data-testid="formfav-top-picks-empty"
        style={{ fontSize: 11, color: "#888", padding: "12px 0", textAlign: "center", lineHeight: 1.4 }}>
        {codeIsGallops ? (
          "No high-confidence model picks on this card yet."
        ) : (
          <>
            FormFav predictions are currently only available for <b>gallops</b>.<br />
            Harness and greyhound predictions are not yet supported by the API.
          </>
        )}
      </div>
    );
  }
  return (
    <div data-testid="formfav-top-picks" style={{ marginTop: 4 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: "#888",
        letterSpacing: "0.08em", textTransform: "uppercase",
        marginBottom: 6, display: "flex", justifyContent: "space-between",
      }}>
        <span>{heading}</span>
        <span style={{ color: "#aaa" }}>{decorated.length} pick{decorated.length === 1 ? "" : "s"}</span>
      </div>
      {decorated.map((p, i) => (
        <TopPickRow key={`${p.slug}-${p.race}-${p.number}-${i}`}
          p={p} rank={i + 1} fmtPct={fmtPct} fmtOdds={fmtOdds}
          onClick={() => onPick(p)}
          token={token} onLogout={onLogout}
          date={date} raceCode={raceCode}
          onPromoted={onPromoted}
          ppPrice={ppPrices?.[(p.name || "").toLowerCase()] || null}
          viewMode={viewMode}
        />
      ))}
    </div>
  );
};

// Parse first colour word from a FormFav racingColours string ("Gold, Purple
// Quarters, Black Cap") into a CSS colour. Falls back to monument grey.
const COLOUR_MAP = {
  red: "#dc2626", crimson: "#b91c1c", maroon: "#7f1d1d", scarlet: "#dc2626",
  pink: "#ec4899", rose: "#f43f5e",
  orange: "#f97316", amber: "#f59e0b", tangerine: "#ea580c",
  yellow: "#eab308", gold: "#ca8a04", cream: "#fef3c7", lemon: "#fde047",
  green: "#16a34a", emerald: "#059669", olive: "#65a30d", lime: "#84cc16",
  teal: "#0d9488", aqua: "#06b6d4", turquoise: "#14b8a6",
  blue: "#2563eb", navy: "#1e3a8a", royal: "#1d4ed8", sky: "#0ea5e9", "light blue": "#38bdf8",
  purple: "#9333ea", violet: "#7c3aed", mauve: "#a78bfa", lavender: "#c4b5fd", lilac: "#a78bfa",
  black: "#171717", grey: "#525252", gray: "#525252", silver: "#94a3b8",
  white: "#f8fafc", cream2: "#fef9c3", beige: "#fef3c7",
  brown: "#78350f", tan: "#a16207", khaki: "#a3a37b",
};
const parseSilksColours = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  // Split first comma-separated segment, lowercase, strip extras.
  const tokens = raw.toLowerCase().split(",")[0].trim().split(/\s+/);
  let primary = null;
  for (const t of tokens) {
    if (COLOUR_MAP[t]) { primary = COLOUR_MAP[t]; break; }
  }
  // Try a secondary colour from the second comma chunk.
  let secondary = null;
  const second = raw.toLowerCase().split(",")[1];
  if (second) {
    for (const t of second.trim().split(/\s+/)) {
      if (COLOUR_MAP[t] && COLOUR_MAP[t] !== primary) {
        secondary = COLOUR_MAP[t]; break;
      }
    }
  }
  return primary ? { primary, secondary: secondary || primary } : null;
};

const SilksSwatch = ({ colours, size = 22 }) => {
  const parsed = parseSilksColours(colours);
  if (!parsed) {
    return (
      <div style={{
        width: size, height: size, borderRadius: 4,
        background: "#e5e5e4", border: "1px solid #d4d4d3", flexShrink: 0,
      }} />
    );
  }
  return (
    <div
      title={colours}
      style={{
        width: size, height: size, borderRadius: 4,
        background: parsed.primary !== parsed.secondary
          ? `linear-gradient(135deg, ${parsed.primary} 50%, ${parsed.secondary} 50%)`
          : parsed.primary,
        border: "1px solid rgba(0,0,0,0.15)", flexShrink: 0,
      }}
    />
  );
};

// V7.5.19 — bet_quality_grade badge.  Bold letter rendered above the
// CONFIDENCE % in the top-right of every betslip card (shortlist /
// track / individual race).  Colour-coded so admins can scan grades
// at a glance:
//   A+ → gold        (premier setups)
//   A  → emerald     (strong picks)
//   B  → amber       (decent shape)
//   C  → slate-grey  (marginal)
//   D  → rose        (low quality, horse_score < 20)
// V7.5.28 — horse_score-only bands (race_reliability removed from
// the formula); badge now also shows the raw score in parentheses,
// e.g. "A+(54)" / "C(-8)", so admins can see exactly where a horse
// sits within its band.
// Renders nothing when no grade is present (snapshot pre-writeback).
const GRADE_COLORS = {
  "A+": "#d4af37",
  "A":  "#16a34a",
  "B":  "#f59e0b",
  "C":  "#94a3b8",
  "D":  "#e11d48",
};
const GradeBadge = ({ grade, score, size = "md", testid }) => {
  if (!grade || !GRADE_COLORS[grade]) return null;
  const fs = size === "lg" ? 22 : size === "sm" ? 14 : 18;
  return (
    <div
      data-testid={testid || `bet-grade-${grade}`}
      title={`Bet Quality Grade: ${grade}${score != null ? ` (horse_score ${score})` : ""}`}
      style={{
        fontSize: fs,
        fontWeight: 900,
        color: GRADE_COLORS[grade],
        lineHeight: 1,
        letterSpacing: "-0.02em",
        fontFamily: "system-ui, -apple-system, 'Helvetica Neue', sans-serif",
      }}
    >
      {grade}{score != null && (
        <span style={{ fontSize: fs * 0.6, fontWeight: 700 }}>({score})</span>
      )}
    </div>
  );
};

const TopPickRow = ({ p, rank, fmtPct, fmtOdds, onClick, token, onLogout, date, raceCode, onPromoted, ppPrice, viewMode = null }) => {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(""); // "ok" | "err" | ""
  const [msg, setMsg] = useState("");
  // PRIMARY = proximity-adjusted FTP (same as sort order, so the bar +
  // big number always agree with the ranking).
  const ftpAdj = p.ftpScoreAdjusted != null ? p.ftpScoreAdjusted
                  : (p.ftpScore != null ? p.ftpScore : null);
  const ftpFair = p.ftpFairOdds;
  const winPct = ftpAdj != null ? Math.min(100, Math.max(0, ftpAdj)) : null;
  // 2026-10-25 — bands validated on settled outcomes (ftp_ratings_log,
  // Jul 2026 set / Sep+Oct 2026 tested): win% rises consistently
  // across <20 (~6-7%) / 20-29 (~9%) / 30-39 (~15-17%) / 40+ (~27-37%).
  // Same 4 existing colours, reassigned lowest→highest to these bands.
  const bg = winPct == null ? "#e5e5e4"
    : winPct >= 40 ? "#16a34a"
    : winPct >= 30 ? "#059669"
    : winPct >= 20 ? "#f59e0b"
    : "#94a3b8";

  // FIRE EMOJI — visual indicator for market shortening (steam).
  // Prefers `firstSeenOddsBest` (our authoritative opening price,
  // captured by the early-morning cron and never overwritten) and
  // falls back to FormStack's published `liveOddsOpen` for cards
  // where the morning cron hasn't run yet (typical for tomorrow's
  // races). Anything ≥10% shorter is a 🔥; ≥20% is 🔥🔥; ≥35% is
  // 🔥🔥🔥 (huge steam). Drift the other way (longer) gets nothing
  // — fire is reserved for confidence-building moves.
  const fireOpening = p.firstSeenOddsBest ?? p.liveOddsOpen ?? null;
  const fireCurrent = p.liveOddsBest ?? null;
  const fireEmoji = (() => {
    if (fireOpening == null || fireCurrent == null) return "";
    if (fireOpening <= 1 || fireCurrent <= 0) return "";
    const drop = (fireOpening - fireCurrent) / fireOpening;
    if (drop >= 0.35) return "\u{1F525}\u{1F525}\u{1F525}";
    if (drop >= 0.20) return "\u{1F525}\u{1F525}";
    if (drop >= 0.10) return "\u{1F525}";
    return "";
  })();
  const fireTitle = (() => {
    if (!fireEmoji) return "";
    const dropPct = ((fireOpening - fireCurrent) / fireOpening) * 100;
    return `Shortened ${dropPct.toFixed(0)}% from open $${Number(fireOpening).toFixed(2)} → $${Number(fireCurrent).toFixed(2)} (market conviction)`;
  })();

  // PUFF / WIND EMOJI — visualises how dominant the top pick is in
  // this slip's race. `confidenceGapToSecond` is the gap (in adjusted
  // FTP points) between rank-1 and rank-2 of the race the slip belongs
  // to. The bigger the gap, the more emphatic the leader. Same scale
  // applies whether the slip itself is rank-1 (BOD/VOD/EOD) or a
  // shorter-priced runner (SMM) — every slip in the same race shares
  // the gap because it reflects the race's leader, not the slip.
  //
  // Thresholds tuned to the user's Arizona Sky reference (raw 81.5,
  // gap-to-2nd 24.7 → 💨💨💨). Tight races and weak-race "gaps" stay
  // dot-free.
  const gap = p.confidenceGapToSecond;
  const puffEmoji = (() => {
    if (gap == null || gap <= 0) return "";
    if (gap >= 22) return "\u{1F4A8}\u{1F4A8}\u{1F4A8}";
    if (gap >= 15) return "\u{1F4A8}\u{1F4A8}";
    if (gap >= 8) return "\u{1F4A8}";
    return "";
  })();
  const puffTitle = puffEmoji
    ? `Top pick is ${gap.toFixed(1)} points clear of the next contender — dominant leader`
    : "";

  const makeOfficial = async (e) => {
    e.stopPropagation();
    if (busy || status === "ok") return;
    setBusy(true); setStatus(""); setMsg("");
    try {
      // Send runner_number so the backend promotes THIS exact horse,
      // not whatever FormFav ranks #1 in the race. Without this, the
      // user clicking Miss McLaren (their FTP pick) ends up with
      // Winchman (FormFav's modelRank=1) on the dashboard.
      const { data } = await axios.post(`${API}/admin/formfav/make_official`, {
        date, track_slug: p.slug, race: p.race, race_code: raceCode || "gallops",
        runner_number: p.number,
      }, { headers: authHeaders(token) });
      setStatus("ok"); setMsg(`Added: ${data?.selection?.horse_name || p.name}`);
      onPromoted && onPromoted(data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      setStatus("err"); setMsg(err?.response?.data?.detail || "Couldn't add");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`top-pick-${rank}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // Keyboard-accessibility — Enter/Space activates same as click.
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick && onClick();
        }
      }}
      style={{
        position: "relative",
        background: "#fff",
        border: `1px solid ${
          p.finish_position === 1 ? "#15803d"
          : p.finish_position != null ? "#c4c4c3"
          : "#e5e5e4"
        }`,
        borderRadius: 10,
        padding: "10px 12px", marginBottom: 8,
        cursor: "pointer",
      }}>
      {/* Finish badge — top-right corner. Lit up once FormStack publishes
          a result for this pick's race. WON green, 2nd blue, 3rd purple,
          others grey. */}
      {p.finish_position != null && (
        <span data-testid={`top-pick-${rank}-finish-badge`}
          style={{
            position: "absolute", top: -8, right: 10,
            background: p.finish_position === 1 ? "#15803d"
                       : p.finish_position === 2 ? "#0369a1"
                       : p.finish_position === 3 ? "#7c3aed" : "#525252",
            color: "#fff", padding: "2px 8px",
            borderRadius: 999, fontSize: 9, fontWeight: 700,
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}>
          {p.finish_position === 1 ? "WON"
            : p.finish_position === 2 ? "2nd"
            : p.finish_position === 3 ? "3rd"
            : `${p.finish_position}th`}
        </span>
      )}
      <div
        style={{
          display: "block", width: "100%", textAlign: "left",
          background: "none", border: "none", padding: 0,
          fontFamily: "inherit",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 22, height: 22, borderRadius: 6, flexShrink: 0,
            background: "#f4f4f3", color: "#323233",
            fontWeight: 800, fontSize: 11,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {rank}
          </div>
          <SilksSwatch colours={p.racingColours} size={22} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Badge row — sits ABOVE the horse name so the name itself
                stays clean. BOD/VOD/EOD/Result/Finish chips wrap here
                instead of competing with the name for horizontal space.
                Hidden entirely when no badge applies. */}
            {(p.isBestOfDay || p.isValueOfDay || p.isEdgeOfDay || p.isGradeOfDay
              || p.isSmartMoneyMover || p.isRawLeader
              || p.resultPosition != null || p.finish_position != null) && (
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                flexWrap: "wrap", marginBottom: 3,
              }}>
                {p.isBestOfDay && (
                  <span data-testid={`top-pick-${rank}-bod-badge`}
                    title="Best of Day — our highest-confidence pick across every meeting today."
                    style={{
                      fontSize: 8, fontWeight: 800, color: "#fff",
                      background: "linear-gradient(90deg,#15803d,#16a34a)",
                      padding: "2px 6px", borderRadius: 3,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}>
                    BOD
                  </span>
                )}
                {p.isValueOfDay && (
                  <span data-testid={`top-pick-${rank}-vod-badge`}
                    title="Value of Day — quality pick whose market price most exceeds our FTP fair odds."
                    style={{
                      fontSize: 8, fontWeight: 800, color: "#fff",
                      background: "linear-gradient(90deg,#b45309,#f59e0b)",
                      padding: "2px 6px", borderRadius: 3,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}>
                    VOD
                  </span>
                )}
                {p.isEdgeOfDay && (
                  <span data-testid={`top-pick-${rank}-eod-badge`}
                    title="Edge of Day — biggest overlay between market price and our FTP fair odds."
                    style={{
                      fontSize: 8, fontWeight: 800, color: "#fff",
                      background: "linear-gradient(90deg,#0369a1,#0ea5e9)",
                      padding: "2px 6px", borderRadius: 3,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}>
                    EOD
                  </span>
                )}
                {/* V7.5.22 — Grade Of Day pill.  Emerald gradient to
                    visually distinguish from BOD (also green) — GOD
                    uses a deeper teal anchor so the two never
                    visually merge. */}
                {p.isGradeOfDay && (
                  <span data-testid={`top-pick-${rank}-god-badge`}
                    title="Grade of Day — A+/A graded horse on a race with reliability ≥ 70. One per race, sorted by horse_score."
                    style={{
                      fontSize: 8, fontWeight: 800, color: "#fff",
                      background: "linear-gradient(90deg,#0d9488,#10b981)",
                      padding: "2px 6px", borderRadius: 3,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}>
                    GOD
                  </span>
                )}
                {/* SMM + RAW chips — track the headline picks of those
                    pillars across every list (matches BOD/VOD/EOD). */}
                {p.isSmartMoneyMover && (
                  <span data-testid={`top-pick-${rank}-smm-badge`}
                    title="Smart Money Movers — biggest sustained market shortening today."
                    style={{
                      fontSize: 8, fontWeight: 800, color: "#fff",
                      background: "linear-gradient(90deg,#15803d,#22c55e)",
                      padding: "2px 6px", borderRadius: 3,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}>
                    SMM
                  </span>
                )}
                {p.isRawLeader && (
                  <span data-testid={`top-pick-${rank}-raw-badge`}
                    title="Raw FTP leader — highest raw rating engine score before any modifiers."
                    style={{
                      fontSize: 8, fontWeight: 800, color: "#fff",
                      background: "linear-gradient(90deg,#4338ca,#6366f1)",
                      padding: "2px 6px", borderRadius: 3,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}>
                    RAW
                  </span>
                )}
                {/* RESULT BADGE — once FormStack has graded the race we
                    surface the finish position. Green for 1st, amber for
                    2nd/3rd, muted grey for outside the placings. */}
                {p.resultPosition != null && (() => {
                  const pos = p.resultPosition;
                  const colour = pos === 1
                    ? "linear-gradient(90deg,#15803d,#16a34a)"
                    : pos <= 3
                      ? "linear-gradient(90deg,#b45309,#f59e0b)"
                      : "#cfcfcf";
                  const label = pos === 1 ? "1st"
                    : pos === 2 ? "2nd"
                    : pos === 3 ? "3rd"
                    : `${pos}th`;
                  return (
                    <span data-testid={`top-pick-${rank}-result-badge`}
                      title={`Finished ${label} in this race (FormStack-graded).`}
                      style={{
                        fontSize: 8, fontWeight: 800,
                        color: pos <= 3 ? "#fff" : "#525252",
                        background: colour,
                        padding: "2px 6px", borderRadius: 3,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}>
                      {label}
                    </span>
                  );
                })()}
              </div>
            )}
            <AdminRaceCountdown
              testid={`top-pick-${rank}-countdown`}
              raceTime={p.race_time}
              state={p.state}
              dateString={p.date_string}
            />
            <div style={{
              fontSize: 13, fontWeight: 700, color: "#323233",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span>#{p.number} {p.name}</span>
              {fireEmoji && (
                <span data-testid={`top-pick-${rank}-fire`}
                  title={fireTitle}
                  style={{
                    fontSize: 12, lineHeight: 1, whiteSpace: "nowrap",
                  }}>
                  {fireEmoji}
                </span>
              )}
              {puffEmoji && (
                <span data-testid={`top-pick-${rank}-puff`}
                  title={puffTitle}
                  style={{
                    fontSize: 12, lineHeight: 1, whiteSpace: "nowrap",
                  }}>
                  {puffEmoji}
                </span>
              )}
            </div>
            <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
              {p.track} · R{p.race}
              {p.race_time && <> · {p.race_time}</>}
              {p.barrier != null && <> · Bar {p.barrier}</>}
              {p.weight != null && <> · {p.weight}kg</>}
            </div>
            {(p.jockey || p.trainer) && (
              <div style={{ fontSize: 10, color: "#666", marginTop: 1,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.jockey && <>J: {p.jockey}</>}
                {p.jockey && p.trainer && " · "}
                {p.trainer && <>T: {p.trainer}</>}
              </div>
            )}
            {/* "best @ <book>" pill removed per user request 2026-05-27 —
                the book name was visual clutter on every slip. Book is
                still displayed inside the expanded race-detail view. */}
          </div>
          <div data-testid={`top-pick-${rank}-rhs-stack`}
            style={{ textAlign: "right", flexShrink: 0, minWidth: 78,
                      display: "flex", flexDirection: "column", alignItems: "flex-end",
                      gap: 2, fontVariantNumeric: "tabular-nums" }}>
            {/* V7.5.19 — BET QUALITY GRADE — bold letter above the
                confidence %.  Sourced from the race-scope snapshot
                writeback (V7.5.18) so it freezes on the day the
                grade was earned. */}
            <GradeBadge
              grade={p.bet_quality_grade}
              score={p.horse_score}
              testid={`top-pick-${rank}-grade`}
            />
            {/* CONFIDENCE % — the model's conviction (FTP Score
                proximity-adjusted, 0-100). Reads more naturally than the
                implied win-probability of the fair-odds quote: a $4
                horse is only 25% implied, but our model is 77%
                confident in it. */}
            {ftpAdj != null && (
              <div data-testid={`top-pick-${rank}-confidence`} style={{
                fontSize: 16, fontWeight: 800, color: bg, lineHeight: 1,
              }}>
                {Math.round(ftpAdj)}%
              </div>
            )}
            <div style={{ fontSize: 7, fontWeight: 700, color: "#888",
                          letterSpacing: "0.08em", textTransform: "uppercase",
                          marginBottom: 3 }}>
              Confidence
            </div>
            {/* FTP MODEL ODDS — the "fair" price our model thinks the
                horse should be. */}
            {ftpFair != null && (
              <div data-testid={`top-pick-${rank}-ftp-odds`} style={{
                fontSize: 11, fontWeight: 700, color: "#525252",
              }}>
                FTP {fmtOdds(ftpFair)}
              </div>
            )}
            {/* TAB / market odds — pulled from FormStack's best-book
                live feed. Falls back to PP price if FormStack hasn't
                opened the market yet (typical for meetings 12+ hours
                from jump). If neither source has data, show a faded
                placeholder so the slip stays balanced. */}
            {(p.liveOddsBest ?? ppPrice) != null ? (
              <div data-testid={`top-pick-${rank}-market-odds`} style={{
                fontSize: 11, fontWeight: 700, color: "#323233",
              }}>
                TAB {fmtOdds(p.liveOddsBest ?? ppPrice)}
              </div>
            ) : (
              <div data-testid={`top-pick-${rank}-market-pending`} style={{
                fontSize: 9, fontWeight: 600, color: "#bbb",
                letterSpacing: "0.04em",
              }}>
                TAB pending
              </div>
            )}
            {/* +/- VALUE diff between FTP and TAB — sits BELOW the TAB
                line per spec. Positive = market is LONGER than our fair
                (value play). Negative = market SHORTER. Only rendered
                when BOTH sides have prices. */}
            {ftpFair != null && (p.liveOddsBest ?? ppPrice) != null && (() => {
              const market = p.liveOddsBest ?? ppPrice;
              const valuePct = ((market - ftpFair) / ftpFair) * 100;
              const valueColor = valuePct >= 5 ? "#0a7d3a"
                : valuePct <= -5 ? "#b91c1c" : "#888";
              return (
                <div data-testid={`top-pick-${rank}-value`} style={{
                  fontSize: 10, fontWeight: 800, color: valueColor,
                  letterSpacing: "0.02em",
                }}>
                  {valuePct > 0 ? "+" : ""}{valuePct.toFixed(0)}%
                </div>
              );
            })()}
            {/* RAW value annotation — surfaces the underlying raw
                ftpScore on EVERY slip (not just the RAW pill). Lets
                the user eyeball the gauge between adjusted (green
                circle, top-right) and raw (this line) on any list. */}
            {p.ftpScore != null && (
              <div data-testid={`top-pick-${rank}-raw-value`} style={{
                fontSize: 9, fontWeight: 700, color: "#4f46e5",
                letterSpacing: "0.04em", marginTop: 2,
              }}>
                RAW {Number(p.ftpScore).toFixed(1)}
              </div>
            )}
          </div>
        </div>
        {winPct != null && (
          <div style={{ height: 4, background: "#f4f4f3", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
            <div style={{
              width: `${winPct}%`, height: "100%", background: bg,
              borderRadius: 999, transition: "width 0.4s ease",
            }} />
          </div>
        )}
      </div>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
        {status === "ok" && (
          <span data-testid={`top-pick-${rank}-status`}
            style={{ fontSize: 10, fontWeight: 700, color: "#0a7d3a", letterSpacing: "0.04em" }}>
            ✓ {msg}
          </span>
        )}
        {status === "err" && (
          <span data-testid={`top-pick-${rank}-status`}
            style={{ fontSize: 10, fontWeight: 700, color: "#b91c1c" }}>
            {msg}
          </span>
        )}
        {status === "" && <span />}
        <button data-testid={`top-pick-${rank}-make-official`}
          type="button" onClick={makeOfficial} disabled={busy || status === "ok"}
          style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", padding: "5px 10px", borderRadius: 999,
            background: status === "ok" ? "#dcfce7" : "#323233",
            color: status === "ok" ? "#0a7d3a" : "#fff",
            border: "none", cursor: busy || status === "ok" ? "default" : "pointer",
            opacity: busy ? 0.6 : 1, fontFamily: "inherit",
            transition: "background 0.15s ease",
          }}>
          {busy ? "Adding…" : status === "ok" ? "Official" : "Make Official"}
        </button>
      </div>
    </div>
  );
};

// Pills helper — used for region + race-code toggles.
const Pills = ({ value, options, onChange, testid, stretch }) => (
  <div data-testid={testid}
    style={{
      display: "inline-flex", padding: 2, background: "#f4f4f3", borderRadius: 999,
      flex: stretch ? 1 : "0 0 auto",
    }}>
    {options.map(([k, label]) => {
      const on = value === k;
      return (
        <button key={k} type="button" onClick={() => onChange(k)}
          style={{
            flex: stretch ? 1 : "0 0 auto",
            background: on ? "#323233" : "transparent",
            color: on ? "#fff" : "#666",
            border: "none", borderRadius: 999,
            padding: "4px 10px", fontSize: 10, fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase",
            cursor: "pointer",
            transition: "background 0.15s ease, color 0.15s ease",
          }}>
          {label}
        </button>
      );
    })}
  </div>
);

// Renders the full race + runner data block.
const FormFavResult = ({ data, fmtPct, fmtOdds, token, onLogout, date, raceCode, ppPrices = {}, trackSlug, raceNumber }) => {
  const tz = data.timezone || "Australia/Sydney";
  const localTime = data.startTime
    ? new Date(data.startTime).toLocaleString("en-AU", {
        timeZone: tz, hour: "2-digit", minute: "2-digit",
        day: "2-digit", month: "short",
      })
    : null;

  return (
    <div data-testid="formfav-result" style={{ marginTop: 6 }}>
      {/* Race-level metadata */}
      <div style={{ fontSize: 12, color: "#666", marginBottom: 4, lineHeight: 1.5 }}>
        <strong style={{ color: "#323233" }}>{data.track}</strong> R{data.raceNumber}
        {data.raceName ? ` · ${data.raceName}` : ""}
        {localTime && <> · {localTime}</>}
        <br />
        {[data.distance, data.condition, data.raceClass, data.weather]
          .filter(Boolean).join(" · ")}
        {data.prizeMoney && <> · ${Number(data.prizeMoney).toLocaleString()}</>}
        {data.numberOfRunners && <> · {data.numberOfRunners} runners</>}
      </div>
      {(data.confidence || data.modelVersion || data.paceScenario) && (
        <div style={{ fontSize: 11, color: "#888", marginBottom: 10 }}>
          {data.confidence && <>Model confidence <strong>{data.confidence}</strong></>}
          {data.modelVersion && <> · v{data.modelVersion}</>}
          {data.paceScenario && <> · pace {data.paceScenario}</>}
          {data.predictionsError && <span style={{ color: "#b91c1c" }}> · {data.predictionsError}</span>}
        </div>
      )}

      {/* Track bias card if provided */}
      {data.trackBias && (
        <div style={{
          fontSize: 11, color: "#444", padding: "8px 10px", marginBottom: 10,
          background: "#fafaf9", borderRadius: 8, border: "1px solid #f4f4f3",
        }}>
          <strong style={{ color: "#323233" }}>Track bias</strong>
          <br />
          {typeof data.trackBias === "string" ? data.trackBias : JSON.stringify(data.trackBias)}
        </div>
      )}

      {/* Result block — appears as soon as FormStack publishes the
          finishers. Shows 1st / 2nd / 3rd with tab number + horse name
          + SP so the admin gets the race outcome at a glance on the
          same card they used to study the form. */}
      {data?.results?.placings?.length > 0 && (
        <div data-testid="race-placings" style={{
          background: "#fff7ed", border: "1px solid #fed7aa",
          borderRadius: 10, padding: "10px 12px", marginBottom: 12,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, color: "#9a3412",
            letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6,
          }}>
            Result
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {data.results.placings.map((p) => {
              const medal = p.position === 1 ? "🥇"
                          : p.position === 2 ? "🥈"
                          : p.position === 3 ? "🥉" : "·";
              return (
                <div key={p.position} data-testid={`placing-${p.position}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    fontSize: 12, color: "#323233",
                  }}>
                  <span style={{ fontSize: 16 }}>{medal}</span>
                  <span style={{ fontWeight: 700, minWidth: 22, fontVariantNumeric: "tabular-nums" }}>
                    #{p.tab_number ?? "?"}
                  </span>
                  <span style={{ fontWeight: 600, flex: 1 }}>{p.horse_name}</span>
                  {p.sp && (
                    <span style={{ fontSize: 10, color: "#666",
                                     fontVariantNumeric: "tabular-nums" }}>
                      {p.sp}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(data.runners || []).map((r) => (
        <RunnerCard key={r.number} r={r} fmtPct={fmtPct} fmtOdds={fmtOdds}
          token={token} onLogout={onLogout}
          date={date} raceCode={raceCode}
          trackSlug={trackSlug}
          raceNumber={raceNumber}
          ppPrice={ppPrices?.[(r.name || "").toLowerCase()] || null}
        />
      ))}
    </div>
  );
};

// ============== Bet-slip styled RunnerCard ==============

// Map FormFav speedMap.expected (e.g. "leader", "on-pace", "midfield",
// "back") to a 0-100 settling position used by the speed bar.
const SPEED_POSITION = {
  leader: 5, leaders: 5, lead: 5, "front-runner": 5,
  "on-pace": 25, "on pace": 25, prominent: 25,
  midfield: 55, mid: 55, "mid-field": 55,
  back: 85, "off-pace": 85, "off pace": 85, settler: 85, rear: 95, last: 98,
};

const speedPercent = (sm) => {
  if (!sm) return null;
  // Prefer numeric "settlingPosition" / "settling" if present.
  if (typeof sm.settlingPosition === "number") {
    // Assume 1 = leader … fieldSize = back. Scale to 0-100.
    return Math.min(100, Math.max(0, ((sm.settlingPosition - 1) / 12) * 100));
  }
  if (typeof sm.settling === "number") {
    return Math.min(100, Math.max(0, ((sm.settling - 1) / 12) * 100));
  }
  const key = (sm.expected || sm.settling || sm.position || (typeof sm === "string" ? sm : "")).toString().toLowerCase().trim();
  return SPEED_POSITION[key] ?? null;
};

const RunnerCard = ({ r, fmtPct, fmtOdds, token, onLogout, date, raceCode, trackSlug, raceNumber, ppPrice }) => {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(""); // "ok" | "err" | ""
  const [statusMsg, setStatusMsg] = useState("");
  const [showMore, setShowMore] = useState(false);
  const isScratched = !!r.scratched;
  // V7.2 Session 20 — hard skip on scratched runners.  The backend
  // already strips scratched runners from the response (V7.2
  // Session 19), but if one ever leaks through (stale cache,
  // partial lock replay, etc.) we don't want it cluttering the
  // admin list with a dimmed (SCR) ghost — return null so the
  // entire card simply disappears.
  if (isScratched) return null;
  const stats = r.stats || {};
  const overall = stats.overall || stats.career;
  const trackS = stats.track;
  const distS = stats.distance;
  const tDistS = stats.trackDistance;
  const conditionS = stats.condition;
  const jockey = r.jockeyStats || {};
  const trainer = r.trainerStats || {};
  const decorators = r.decorators || r.badges || [];
  const sm = r.speedMap;
  const classProfile = r.classProfile;
  // TOP PICK badge follows the FTP-adjusted ranking (proximity + market
  // modulated). Backend was previously assigning ftpRank from RAW
  // ftpScore, which let a heavily-penalised horse (raw 74 / adj 35)
  // steal the badge from a clean adjusted leader (adj 53). Fixed in
  // rating.score_runners — this is the front-end of that fix.
  // V7.2 Session 23 — TOP PICK is now driven by ``confidenceRank``
  // (the blended pillar + market score, the final user-visible
  // ranking).  Falls back to ftpRank for legacy snapshots without
  // confidenceRank populated.
  const displayRank = r.confidenceRank ?? r.ftpRank;
  const isTop = displayRank === 1 && !isScratched;
  const ftpRaw = r.ftpScore != null ? r.ftpScore : null;
  const ftpAdj = r.ftpScoreAdjusted != null ? r.ftpScoreAdjusted : ftpRaw;
  const ftpPct = ftpAdj != null ? Math.min(100, Math.max(0, ftpAdj)) : null;
  const speedPct = speedPercent(sm);
  const edgePct = r.edge != null ? r.edge * 100 : null;
  const edgePos = edgePct != null && edgePct > 0;
  // Live TAB / market odds — shown next to FTP fair so admin can spot
  // the value differential at a glance without expanding.
  const marketOdds = r.liveOddsBest || r.marketOdds || ppPrice;

  // Confidence bar colour ramps from amber → green as FTP Score climbs.
  // 2026-10-25 — bands validated on settled outcomes (ftp_ratings_log,
  // Jul 2026 set / Sep+Oct 2026 tested): win% rises consistently
  // across <20 (~6-7%) / 20-29 (~9%) / 30-39 (~15-17%) / 40+ (~27-37%).
  // Same 4 existing colours, reassigned lowest→highest to these bands.
  const confidenceBg = ftpPct == null ? "#e5e5e4"
    : ftpPct >= 40 ? "#16a34a"
    : ftpPct >= 30 ? "#059669"
    : ftpPct >= 20 ? "#f59e0b" : "#94a3b8";

  // FINISH BADGE — once the race settles, we tag every runner with
  // the position they crossed in (data flows from FormStack →
  // _attach_race_finishers → r.finish_position).
  const finishPos = r.finish_position;
  const finishBadge = finishPos == null ? null : (
    <span data-testid={`runner-${r.number}-finish-badge`}
      style={{
        position: "absolute", top: -8, right: 12,
        background: finishPos === 1 ? "#15803d"
                   : finishPos === 2 ? "#0369a1"
                   : finishPos === 3 ? "#7c3aed" : "#525252",
        color: "#fff", padding: "2px 8px",
        borderRadius: 999, fontSize: 9, fontWeight: 700,
        letterSpacing: "0.08em", textTransform: "uppercase",
      }}>
      {finishPos === 1 ? "WON" : finishPos === 2 ? "2nd" : finishPos === 3 ? "3rd" : `${finishPos}th`}
    </span>
  );

  return (
    <div
      data-testid={`formfav-runner-${r.number}`}
      style={{
        position: "relative",
        background: "#fff",
        border: `1px solid ${
          finishPos === 1 ? "#15803d"
          : finishPos != null ? "#c4c4c3"
          : isTop ? "#323233" : "#e5e5e4"
        }`,
        borderRadius: 12,
        padding: "12px 14px",
        marginBottom: 10,
        opacity: isScratched ? 0.45 : 1,
        boxShadow: isTop ? "0 2px 8px rgba(50,50,51,0.12)" : "0 1px 2px rgba(0,0,0,0.03)",
      }}>
      {isTop && (
        <span style={{
          position: "absolute", top: -8, left: 12,
          background: "#323233", color: "#fff", padding: "2px 8px",
          borderRadius: 999, fontSize: 9, fontWeight: 700,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          Top Pick
        </span>
      )}
      {finishBadge}

      {/* COLLAPSED HEADER — sportsbet-style:
          [tab#] [silk] [name + J/T + bar/weight]  →  [FTP] [Fair $] [TAB $] */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8,
          background: isTop ? "#323233" : "#f4f4f3",
          color: isTop ? "#fff" : "#323233",
          fontWeight: 800, fontSize: 14,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, fontVariantNumeric: "tabular-nums",
        }}>
          {r.number ?? "·"}
        </div>
        {r.silk_url && (
          <img
            src={r.silk_url}
            alt=""
            data-testid={`runner-${r.number}-silk`}
            style={{ width: 34, height: 34, borderRadius: 6, objectFit: "contain",
                     background: "#f4f4f3", flexShrink: 0 }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: "#323233",
            lineHeight: 1.2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {r.name || "—"}{isScratched && <span style={{ color: "#b91c1c", marginLeft: 6 }}>(SCR)</span>}
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            J: {r.jockey || "—"}{r.claim ? ` (a${r.claim})` : ""} · T: {r.trainer || "—"}
          </div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>
            Bar {r.barrier ?? "—"}
            {r.weight && <> · {r.weight}kg</>}
            {r.age && <> · {r.age}yo</>}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0, minWidth: 78,
                       display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
          {/* V7.5.19 — BET QUALITY GRADE badge above the FTP score. */}
          <GradeBadge
            grade={r.bet_quality_grade}
            score={r.horse_score}
            testid={`runner-${r.number}-grade`}
          />
          <div style={{ fontSize: 17, fontWeight: 800, color: "#323233", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
            {ftpAdj != null ? ftpAdj.toFixed(1) : "—"}
          </div>
          <div style={{ fontSize: 9, fontWeight: 600, color: "#888", letterSpacing: "0.06em", textTransform: "uppercase" }}>
            FTP
          </div>
          {r.ftpFairOdds != null && (
            <div style={{ fontSize: 11, color: "#323233", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              <span style={{ color: "#888", fontSize: 9, fontWeight: 700,
                              letterSpacing: "0.06em", textTransform: "uppercase",
                              marginRight: 4 }}>Fair</span>
              {fmtOdds(r.ftpFairOdds)}
            </div>
          )}
          {marketOdds != null && (
            <div style={{ fontSize: 11, color: "#323233", fontVariantNumeric: "tabular-nums" }}>
              <span style={{ color: "#888", fontSize: 9, fontWeight: 700,
                              letterSpacing: "0.06em", textTransform: "uppercase",
                              marginRight: 4 }}>TAB</span>
              {fmtOdds(marketOdds)}
            </div>
          )}
          {/* MARKET-vs-FAIR EDGE — mirrors the small TopPickCard's
              value diff so the bigger race-analysis slip cards show
              the same overlay info at a glance. Computed against
              displayed (anchored) fair so the % matches what the
              admin reads off the price line above. */}
          {r.ftpFairOdds != null && marketOdds != null && (() => {
            const valuePct = ((marketOdds - r.ftpFairOdds) / r.ftpFairOdds) * 100;
            const valueColor = valuePct >= 5 ? "#0a7d3a"
              : valuePct <= -5 ? "#b91c1c" : "#888";
            return (
              <div data-testid={`formfav-runner-${r.number}-value-diff`}
                style={{
                  fontSize: 10, fontWeight: 800, color: valueColor,
                  letterSpacing: "0.02em", marginTop: 2,
                }}>
                {valuePct > 0 ? "+" : ""}{valuePct.toFixed(1)}%
              </div>
            );
          })()}
        </div>
      </div>

      {/* Confidence bar — always visible in collapsed view */}
      {ftpPct != null && (
        <div style={{ marginTop: 10 }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "baseline",
            fontSize: 9, fontWeight: 700, color: "#888",
            letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3,
          }}>
            <span title={
              "FTP = proprietary 10-factor score, proximity + market modulated.\n" +
              "RAW = own score from form/class/jockey/etc. Adjusted score is what " +
              "the rankings sort on."
            } style={{ cursor: "help", borderBottom: "1px dotted #ccc" }}>
              FTP Score
            </span>
            <span style={{ color: "#323233", fontVariantNumeric: "tabular-nums" }}>
              {ftpAdj.toFixed(1)}
              {ftpRaw != null && ftpAdj < ftpRaw - 0.5 && (
                <span style={{ color: "#888", marginLeft: 4, fontWeight: 600 }}>
                  ({ftpRaw.toFixed(1)} raw)
                </span>
              )}
            </span>
          </div>
          <div style={{ height: 6, background: "#f4f4f3", borderRadius: 999, overflow: "hidden" }}>
            <div data-testid={`runner-${r.number}-confidence-bar`}
              style={{
                width: `${ftpPct}%`, height: "100%",
                background: confidenceBg,
                borderRadius: 999,
                transition: "width 0.4s ease",
              }} />
          </div>
        </div>
      )}

      {/* EXPANDED — only when `showMore` is toggled on. Speed map +
          career/track/dist/cond stats + jockey/trainer 30d + decorators
          + class profile. */}
      {showMore && (
        <>
          {speedPct != null && (
            <div style={{ marginTop: 10 }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                fontSize: 9, fontWeight: 700, color: "#888",
                letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3,
              }}>
                <span>Speed Map</span>
                <span style={{ color: "#323233" }}>
                  {sm?.expected || sm?.settling || sm?.position || "—"}
                </span>
              </div>
              <div style={{
                position: "relative", height: 6, background: "#f4f4f3",
                borderRadius: 999, overflow: "visible",
              }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0, right: 0,
                  background: "linear-gradient(to right, #fee2e2 0%, #fef3c7 50%, #dbeafe 100%)",
                  borderRadius: 999, opacity: 0.55,
                }} />
                <div data-testid={`runner-${r.number}-speed-marker`}
                  style={{
                    position: "absolute",
                    left: `calc(${speedPct}% - 6px)`,
                    top: -3, width: 12, height: 12, borderRadius: "50%",
                    background: "#323233", border: "2px solid #fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    transition: "left 0.4s ease",
                  }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8, color: "#aaa", marginTop: 2 }}>
                <span>Lead</span><span>Mid</span><span>Back</span>
              </div>
            </div>
          )}

          {(edgePct != null || r.form) && (
            <div style={{
              display: "flex", gap: 6, marginTop: 10,
              fontSize: 10, fontWeight: 700,
            }}>
              {edgePct != null && (
                <span data-testid={`runner-${r.number}-edge-badge`}
                  style={{
                    padding: "3px 8px", borderRadius: 6,
                    background: edgePos ? "#dcfce7" : "#fef2f2",
                    color: edgePos ? "#0a7d3a" : "#b91c1c",
                    letterSpacing: "0.04em",
                  }}>
                  {edgePos ? "+" : ""}{edgePct.toFixed(1)}% edge
                </span>
              )}
              {r.form && (
                <span style={{ padding: "3px 8px", borderRadius: 6, background: "#f4f4f3", color: "#666", fontFamily: "monospace", letterSpacing: "0.05em" }}>
                  {r.form}
                </span>
              )}
            </div>
          )}

          {(() => {
            const validStat = (s) => s && typeof s.starts === "number" && s.starts > 0;
            const hasPrize = r.careerPrizeMoney != null && Number.isFinite(Number(r.careerPrizeMoney)) && Number(r.careerPrizeMoney) > 0;
            const showStats = validStat(overall) || validStat(trackS) || validStat(distS) || validStat(tDistS) || validStat(conditionS) || hasPrize;
            if (!showStats) return null;
            return (
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "2px 12px",
                fontSize: 10, color: "#666", marginTop: 8,
              }}>
                {validStat(overall) && <span>Career {overall.starts}:{overall.wins || 0}-{overall.seconds || 0}-{overall.thirds || 0}</span>}
                {validStat(trackS) && <span>Track {trackS.starts}:{trackS.wins || 0}-{trackS.seconds || 0}-{trackS.thirds || 0}</span>}
                {validStat(distS) && <span>Dist {distS.starts}:{distS.wins || 0}-{distS.seconds || 0}-{distS.thirds || 0}</span>}
                {validStat(tDistS) && <span>T+D {tDistS.starts}:{tDistS.wins || 0}-{tDistS.seconds || 0}-{tDistS.thirds || 0}</span>}
                {validStat(conditionS) && <span>Cond {conditionS.starts}:{conditionS.wins || 0}-{conditionS.seconds || 0}-{conditionS.thirds || 0}</span>}
                {hasPrize && <span>$Earned {Number(r.careerPrizeMoney).toLocaleString()}</span>}
              </div>
            );
          })()}

          {(jockey.last30?.starts > 0 || trainer.last30?.starts > 0) && (
            <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>
              {jockey.last30?.starts > 0 && (
                <span>J 30d {jockey.last30.starts}:{jockey.last30.wins || 0} ({((jockey.last30.strikeRate || 0) * 100).toFixed(0)}%)</span>
              )}
              {trainer.last30?.starts > 0 && (
                <span>{jockey.last30?.starts > 0 ? " · " : ""}T 30d {trainer.last30.starts}:{trainer.last30.wins || 0} ({((trainer.last30.strikeRate || 0) * 100).toFixed(0)}%)</span>
              )}
            </div>
          )}

          {Array.isArray(decorators) && decorators.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
              {decorators.map((b, i) => (
                <span key={i}
                  title={b.description || b.label || ""}
                  style={{
                    fontSize: 9, fontWeight: 700, padding: "2px 6px",
                    background: "#323233", color: "#fff", borderRadius: 999,
                    letterSpacing: "0.04em", textTransform: "uppercase",
                  }}>
                  {b.label || b.code || (typeof b === "string" ? b : JSON.stringify(b))}
                </span>
              ))}
            </div>
          )}

          {(() => {
            if (!classProfile) return null;
            if (typeof classProfile === "string") {
              const trimmed = classProfile.trim();
              if (!trimmed || trimmed === "—") return null;
              return (
                <div style={{ fontSize: 10, color: "#666", marginTop: 6 }}>
                  Class profile: {trimmed}
                </div>
              );
            }
            const rating = classProfile.rating;
            if (rating == null) return null;
            return (
              <div style={{ fontSize: 10, color: "#666", marginTop: 6 }}>
                Class profile: {rating}{classProfile.suited != null && <> · {classProfile.suited ? "suited" : "unsuited"}</>}
              </div>
            );
          })()}
        </>
      )}

      {/* Show more / less toggle — only show if there's expandable content */}
      {(speedPct != null || overall || trackS || distS || tDistS || conditionS
        || jockey.last30?.starts > 0 || trainer.last30?.starts > 0
        || (Array.isArray(decorators) && decorators.length > 0)
        || classProfile) && (
        <button
          type="button"
          data-testid={`runner-${r.number}-show-more`}
          onClick={() => setShowMore((v) => !v)}
          style={{
            marginTop: 8, background: "transparent", border: "none",
            color: "#525252", fontSize: 10, fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase",
            cursor: "pointer", padding: 0, fontFamily: "inherit",
          }}>
          {showMore ? "Show less ↑" : "Show more ↓"}
        </button>
      )}

      {/* Make Official — always visible in collapsed view */}
      {!isScratched && trackSlug && raceNumber != null && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8, marginTop: 10, paddingTop: 8, borderTop: "1px dashed #e5e5e4",
        }}>
          {status === "ok" && (
            <span data-testid={`runner-${r.number}-make-official-status`}
              style={{ fontSize: 10, fontWeight: 700, color: "#0a7d3a", letterSpacing: "0.04em" }}>
              ✓ {statusMsg}
            </span>
          )}
          {status === "err" && (
            <span data-testid={`runner-${r.number}-make-official-status`}
              style={{ fontSize: 10, fontWeight: 700, color: "#b91c1c" }}>
              {statusMsg}
            </span>
          )}
          {status === "" && <span style={{ fontSize: 10, color: "#888" }}>Promote this runner →</span>}
          <button
            data-testid={`runner-${r.number}-make-official`}
            type="button"
            disabled={busy || status === "ok"}
            onClick={async () => {
              setBusy(true); setStatus(""); setStatusMsg("");
              try {
                const { data: resp } = await axios.post(
                  `${API}/admin/formfav/make_official`,
                  {
                    date, track_slug: trackSlug,
                    race: Number(raceNumber),
                    race_code: raceCode || "gallops",
                    runner_number: Number(r.number),
                  },
                  { headers: authHeaders(token) },
                );
                setStatus("ok");
                setStatusMsg(`Added: ${resp?.selection?.horse_name || r.name}`);
              } catch (err) {
                if (err?.response?.status === 401) onLogout?.();
                setStatus("err");
                setStatusMsg(err?.response?.data?.detail || "Couldn't add");
              } finally { setBusy(false); }
            }}
            style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
              textTransform: "uppercase", padding: "5px 10px", borderRadius: 999,
              background: status === "ok" ? "#dcfce7" : "#323233",
              color: status === "ok" ? "#0a7d3a" : "#fff",
              border: "none", cursor: busy || status === "ok" ? "default" : "pointer",
              opacity: busy ? 0.6 : 1, fontFamily: "inherit",
            }}>
            {busy ? "Adding…" : status === "ok" ? "Official" : "Make Official"}
          </button>
        </div>
      )}
    </div>
  );
};


// ============== Login Modal ==============

const LoginModal = ({ open, onClose, onSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setUsername(""); setPassword(""); setError(""); setBusy(false); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        username: username.trim(),
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-login-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(20, 20, 22, 0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        style={{
          background: "#fff", borderRadius: 14, padding: 24,
          width: "100%", maxWidth: 360,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#323233" }}>Admin Sign In</h3>
          <button type="button" onClick={onClose} aria-label="Close"
            data-testid="admin-login-close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}>
            <X size={18} weight="bold" />
          </button>
        </div>
        <label style={{ display: "block", fontSize: 11, color: "#888", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>Username</label>
        <input data-testid="admin-username-input" autoFocus value={username}
          onChange={(e) => setUsername(e.target.value)} disabled={busy}
          autoCapitalize="off" autoCorrect="off" autoComplete="username" spellCheck={false}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e5e4",
            fontSize: 14, marginBottom: 12, fontFamily: "inherit" }} />
        <label style={{ display: "block", fontSize: 11, color: "#888", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>Password</label>
        <input data-testid="admin-password-input" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} disabled={busy}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e5e4",
            fontSize: 14, marginBottom: 12, fontFamily: "inherit" }} />
        {error && <div data-testid="admin-login-error"
          style={{ background: "#fef2f2", color: "#b91c1c", padding: "8px 12px",
            borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <button type="submit" disabled={busy || !username || !password}
          data-testid="admin-login-submit"
          style={{
            width: "100%", padding: "10px 14px", background: "#323233",
            color: "#fff", border: "none", borderRadius: 8, fontWeight: 700,
            fontSize: 14, cursor: busy ? "not-allowed" : "pointer",
            opacity: busy || !username || !password ? 0.6 : 1,
          }}>
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

// ============== Chat Panel ==============

const ChatBubble = ({ role, content, toolCalls, attachments, animate }) => {
  const isUser = role === "user";
  // Typewriter effect on freshly-arrived assistant bubbles so the
  // reply unspools word-by-word (≈45ms/word). Keeps the conversation
  // feeling alive while we wait for true SSE streaming. User bubbles
  // and re-rendered history skip the animation.
  const [displayText, setDisplayText] = useState(
    isUser || !animate ? (content || "") : ""
  );
  useEffect(() => {
    if (isUser || !animate) {
      setDisplayText(content || "");
      return;
    }
    const words = (content || "").split(/(\s+)/);
    let i = 0;
    setDisplayText("");
    const id = setInterval(() => {
      i += 1;
      if (i > words.length) { clearInterval(id); return; }
      setDisplayText(words.slice(0, i).join(""));
    }, 22);
    return () => clearInterval(id);
  }, [content, isUser, animate]);
  return (
    <div data-testid={`chat-msg-${role}`}
      style={{
        display: "flex", flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start", marginBottom: 10,
      }}>
      <div style={{
        maxWidth: "85%", padding: "10px 14px", borderRadius: 12,
        background: isUser ? "#323233" : "#f4f4f3",
        color: isUser ? "#fff" : "#323233",
        fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap",
        border: isUser ? "none" : "1px solid #e5e5e4",
      }}>
        {displayText}{(!isUser && animate && displayText.length < (content || "").length) && (
          <span style={{ opacity: 0.4 }}>▍</span>
        )}
        {attachments && attachments.length > 0 && (
          <div style={{
            marginTop: 6, paddingTop: 6,
            borderTop: `1px solid ${isUser ? "rgba(255,255,255,0.15)" : "#e5e5e4"}`,
            fontSize: 11, opacity: 0.85,
          }}>
            {attachments.map((a, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {a.includes("pdf") ? <FilePdf size={12} /> : <ImageIcon size={12} />}
                <span style={{ fontFamily: "monospace" }}>{a}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {toolCalls && toolCalls.length > 0 && toolCalls.some((t) => t?.result?.error) && (
        <div style={{ fontSize: 10, color: "#b91c1c", marginTop: 4, fontFamily: "monospace" }}>
          {toolCalls.filter((t) => t?.result?.error).map((t, i) => (
            <span key={i} style={{ marginRight: 6, padding: "1px 6px", background: "#fef2f2", borderRadius: 4 }}>
              {t.name} ⚠
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const MAX_FILES = 4;
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];

// ============== Master Prompt Modal ==============
// Lets the admin edit the LLM system instruction shipped at the top of
// the public /api/ai_assist payload (and copied via the "Copy Master
// Prompt" button on the landing page).
const MasterPromptModal = ({ open, onClose, token, onLogout }) => {
  const [prompt, setPrompt] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  // Hold ``onLogout`` in a ref so the loader effect below isn't re-fired
  // every time the parent re-renders (which would clobber any typed
  // edits and flip the modal back to "Loading…" — user-reported
  // 2026-02-09).
  const onLogoutRef = useRef(onLogout);
  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);

  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/master_prompt`, {
          headers: authHeaders(token),
        });
        if (cancelled) return;
        setPrompt(data?.prompt || "");
        setDefaultPrompt(data?.default_prompt || "");
        setIsDefault(!!data?.is_default);
      } catch (err) {
        if (err?.response?.status === 401) { onLogoutRef.current?.(); return; }
        if (!cancelled) setError(err?.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  const handleSave = async () => {
    if (!prompt.trim()) {
      setError("Prompt cannot be empty");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/master_prompt`,
        { prompt },
        { headers: authHeaders(token) });
      setIsDefault(!!data?.is_default);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      if (err?.response?.status === 401) { onLogoutRef.current?.(); return; }
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset the master prompt to the default? Your custom prompt will be lost.")) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/master_prompt/reset`, {},
        { headers: authHeaders(token) });
      setPrompt(data?.prompt || defaultPrompt);
      setIsDefault(true);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      if (err?.response?.status === 401) { onLogoutRef.current?.(); return; }
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div data-testid="master-prompt-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        zIndex: 220, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, width: "100%", maxWidth: 720,
          maxHeight: "92vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid #ececeb",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PencilSimple size={18} weight="bold" color="#323233" />
            <div style={{ fontSize: 15, fontWeight: 700, color: "#323233" }}>
              Edit Master Prompt
            </div>
            {!isDefault && (
              <span data-testid="master-prompt-custom-badge" style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                textTransform: "uppercase", color: "#0f766e",
                background: "#ccfbf1", padding: "2px 6px", borderRadius: 4,
              }}>Custom</span>
            )}
          </div>
          <button data-testid="master-prompt-close-btn" onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#888" }}>
            <X size={18} weight="bold" />
          </button>
        </div>

        <div style={{ padding: "14px 20px", fontSize: 12, color: "#666", lineHeight: 1.5 }}>
          This is the system instruction shipped at the top of <code style={{
            background: "#f4f4f3", padding: "1px 5px", borderRadius: 4, fontSize: 11,
          }}>/api/ai_assist</code> and copied via the “Copy Master Prompt” button on the AI Assist landing page.
        </div>

        <div style={{ padding: "0 20px 12px", flex: 1, overflow: "hidden", display: "flex" }}>
          {loading ? (
            <div data-testid="master-prompt-loading" style={{ color: "#888", fontSize: 13, padding: "20px 0" }}>
              Loading…
            </div>
          ) : (
            <textarea
              data-testid="master-prompt-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={saving}
              spellCheck={false}
              style={{
                flex: 1, width: "100%", minHeight: 320, maxHeight: "60vh",
                padding: "12px 14px", borderRadius: 8, border: "1px solid #e5e5e4",
                fontSize: 12.5, lineHeight: 1.55, resize: "vertical", outline: "none",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                color: "#222", background: "#fafaf9",
              }}
            />
          )}
        </div>

        {error && (
          <div data-testid="master-prompt-error" style={{
            margin: "0 20px 8px", padding: "8px 12px", background: "#fef2f2",
            border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 6, fontSize: 12,
          }}>{error}</div>
        )}

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px", borderTop: "1px solid #ececeb", gap: 8,
        }}>
          <button data-testid="master-prompt-reset-btn" onClick={handleReset}
            disabled={saving || loading || isDefault}
            style={{
              background: "#fff", color: "#666", border: "1px solid #e5e5e4",
              borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: (saving || loading || isDefault) ? "not-allowed" : "pointer",
              opacity: (saving || loading || isDefault) ? 0.5 : 1, fontFamily: "inherit",
            }}>
            Reset to Default
          </button>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {savedFlash && (
              <span data-testid="master-prompt-saved" style={{
                fontSize: 12, color: "#0f766e", display: "inline-flex", alignItems: "center", gap: 4,
              }}>
                <CheckCircle size={14} weight="fill" /> Saved
              </span>
            )}
            <button onClick={onClose}
              style={{
                background: "#fff", color: "#666", border: "1px solid #e5e5e4",
                borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer",
                fontFamily: "inherit",
              }}>
              Close
            </button>
            <button data-testid="master-prompt-save-btn" onClick={handleSave}
              disabled={saving || loading || !prompt.trim()}
              style={{
                background: "#323233", color: "#fff", border: "1px solid #323233",
                borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600,
                cursor: (saving || loading || !prompt.trim()) ? "not-allowed" : "pointer",
                opacity: (saving || loading || !prompt.trim()) ? 0.6 : 1, fontFamily: "inherit",
              }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const ChatPanel = ({ open, onClose, token, onLogout, onDataChanged }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]); // [{file, label}]
  const [attachError, setAttachError] = useState("");
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  // When collapsed, the chat scroll area + composer hide and the FormFav
  // panel takes the full panel height. Saved between sessions so the
  // admin's preference persists.
  const [chatCollapsed, setChatCollapsed] = useState(() => {
    try { return localStorage.getItem("ftp_chat_collapsed") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("ftp_chat_collapsed", chatCollapsed ? "1" : "0"); }
    catch { /* ignore */ }
  }, [chatCollapsed]);
  const fileInputRef = useRef(null);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || newSessionId(); }
    catch { return newSessionId(); }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (sessionId) try { localStorage.setItem(SESSION_KEY, sessionId); } catch { /* ignore */ }
  }, [sessionId]);

  // Stable ref to onLogout so the history-loader effect below doesn't
  // re-fire every render when the parent recreates the callback.
  // Without this, the panel was hitting /admin/chat/history in a tight
  // loop whenever the parent re-rendered (user-reported 2026-05-29).
  const onLogoutRef = useRef(onLogout);
  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);

  // Load history when panel opens. Deliberately depends ONLY on
  // (open, token, sessionId) — onLogout is read via ref so its
  // identity doesn't drive the effect.
  useEffect(() => {
    if (!open || !token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/chat/history`, {
          headers: authHeaders(token), params: { session_id: sessionId },
        });
        const hist = (data?.history || []).map((h) => ({
          role: h.role, content: h.content, toolCalls: h.tool_calls,
          attachments: h.attachment_summaries,
          _seen: true,  // history is already-rendered, skip typewriter
        }));
        setMessages(hist);
      } catch (err) {
        if (err?.response?.status === 401) onLogoutRef.current?.();
      }
    })();
  }, [open, token, sessionId]);

  // Auto-scroll — but ONLY when the user is already pinned to the
  // bottom. If they've scrolled up to read history, leave them alone.
  // This fixes the "scroll up → bounces back down" bug the admin saw
  // when reading older messages on mobile.
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    // Only snap to bottom if user was within 80px of the bottom (i.e.
    // they're following the conversation, not browsing history).
    if (distanceFromBottom <= 80) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, busy]);

  const onPickFiles = (e) => {
    setAttachError("");
    const picked = Array.from(e.target.files || []);
    e.target.value = ""; // reset so the same file can be re-picked
    const remainingSlots = MAX_FILES - files.length;
    const accepted = [];
    for (const f of picked.slice(0, remainingSlots)) {
      if (!ALLOWED_MIMES.includes(f.type)) {
        setAttachError(`Unsupported file type: ${f.name}`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        setAttachError(`${f.name} exceeds 8 MB`);
        continue;
      }
      accepted.push({ file: f, label: `${f.type.startsWith("image") ? "image" : "pdf"}: ${f.name}` });
    }
    if (picked.length > remainingSlots) {
      setAttachError(`Maximum ${MAX_FILES} attachments per message`);
    }
    if (accepted.length) setFiles((f) => [...f, ...accepted]);
  };

  const removeFile = (idx) => {
    setFiles((f) => f.filter((_, i) => i !== idx));
  };

  const send = useCallback(async () => {
    const text = input.trim();
    if ((!text && files.length === 0) || busy) return;
    setBusy(true);
    const localFiles = files;
    const localText = text || "(see attached file)";
    setInput("");
    setFiles([]);
    setAttachError("");
    setMessages((m) => [...m, {
      role: "user", content: localText,
      attachments: localFiles.map((f) => f.label),
    }]);

    try {
      let data;
      if (localFiles.length === 0) {
        const resp = await axios.post(`${API}/admin/chat`,
          { message: localText, session_id: sessionId },
          { headers: authHeaders(token) });
        data = resp.data;
      } else {
        const fd = new FormData();
        fd.append("message", localText);
        fd.append("session_id", sessionId);
        for (const f of localFiles) fd.append("files", f.file, f.file.name);
        const resp = await axios.post(`${API}/admin/chat/upload`, fd, {
          headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
        });
        data = resp.data;
      }
      setMessages((m) => [...m, {
        role: "assistant", content: data.reply, toolCalls: data.tool_calls,
      }]);
      const wrote = (data.tool_calls || []).some(
        (c) => ["add_horse", "edit_horse", "remove_horse"].includes(c.name) && !c.result?.error,
      );
      if (wrote && onDataChanged) onDataChanged();
    } catch (err) {
      if (err?.response?.status === 401) { onLogout(); return; }
      const detail = err?.response?.data?.detail || err.message;
      setMessages((m) => [...m, {
        role: "assistant", content: `Error: ${detail}`,
      }]);
    } finally { setBusy(false); }
  }, [input, files, busy, sessionId, token, onLogout, onDataChanged]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const clearHistory = async () => {
    if (!window.confirm("Clear this conversation?")) return;
    try {
      await axios.delete(`${API}/admin/chat/history`, {
        headers: authHeaders(token), params: { session_id: sessionId },
      });
      setMessages([]);
      const fresh = newSessionId();
      try { localStorage.setItem(SESSION_KEY, fresh); } catch { /* ignore */ }
      setSessionId(fresh);
    } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div data-testid="admin-chat-panel"
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "100%", maxWidth: 460, zIndex: 190,
        background: "#fff", boxShadow: "-12px 0 40px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
        height: "100dvh", overscrollBehavior: "contain",
      }}>
      <header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: chatCollapsed ? "6px 10px" : "14px 18px",
        borderBottom: "1px solid #e5e5e4", flexShrink: 0,
        transition: "padding 0.2s ease",
      }}>
        {chatCollapsed ? (
          <>
            <button data-testid="chat-collapse-btn"
              onClick={() => setChatCollapsed(false)}
              title="Expand chat"
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "4px 8px", color: "#888", display: "flex",
                alignItems: "center", gap: 6, fontFamily: "inherit", fontSize: 11,
                fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
              }}>
              <Robot size={14} weight="fill" color="#323233" /> Chat
              <CaretDown size={12} weight="bold" />
            </button>
            <button data-testid="chat-close-btn" onClick={onClose} title="Close"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#888" }}>
              <X size={16} weight="bold" />
            </button>
          </>
        ) : (
          <>
            <Robot size={22} weight="fill" color="#323233" />
            <div style={{ display: "flex", gap: 4 }}>
              <button data-testid="chat-collapse-btn"
                onClick={() => setChatCollapsed(true)}
                title="Collapse chat — show more picks"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#888" }}>
                <CaretUp size={16} weight="bold" />
              </button>
              <button data-testid="chat-analytics-btn" onClick={() => setAnalyticsOpen(true)}
                title="Site visits"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#888" }}>
                <ChartBar size={16} weight="bold" />
              </button>
              <button data-testid="chat-master-prompt-btn" onClick={() => setPromptOpen(true)}
                title="Edit master prompt"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#888" }}>
                <PencilSimple size={16} weight="bold" />
              </button>
              <button data-testid="chat-clear-btn" onClick={clearHistory}
                title="Clear conversation"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#888" }}>
                <Trash size={16} weight="bold" />
              </button>
              <button data-testid="chat-logout-btn" onClick={onLogout}
                title="Sign out"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#888" }}>
                <SignOut size={16} weight="bold" />
              </button>
              <button data-testid="chat-close-btn" onClick={onClose}
                title="Close"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#888" }}>
                <X size={18} weight="bold" />
              </button>
            </div>
          </>
        )}
      </header>

      {!chatCollapsed && <>
      <div ref={scrollRef} data-testid="chat-scroll-area"
        style={{
          flex: 1, overflowY: "auto", padding: 16,
          background: "#fafaf9",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          minHeight: 0,
        }}>
        {messages.length === 0 && !busy && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            color: "#999", fontSize: 12, padding: "32px 16px",
            minHeight: "100%", textAlign: "center",
          }}>
            <Robot size={48} weight="thin" style={{ opacity: 0.3, marginBottom: 12 }} />
            <p style={{ margin: 0, maxWidth: 260 }}>
              Ask anything about today's card, or instruct me to add, edit, or remove horses.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <ChatBubble key={i} role={m.role} content={m.content}
            toolCalls={m.toolCalls}
            animate={m.role === "assistant" && i === messages.length - 1 && !m._seen}
          />
        ))}
        {busy && (
          <div data-testid="chat-thinking" style={{ fontSize: 12, color: "#888", padding: 8 }}>
            Thinking…
          </div>
        )}
      </div>

      <div style={{
        borderTop: "1px solid #e5e5e4", padding: 12,
        display: "flex", flexDirection: "column", gap: 6,
        flexShrink: 0, background: "#fff",
      }}>
        {(files.length > 0 || attachError) && (
          <div data-testid="chat-attachments" style={{
            display: "flex", flexWrap: "wrap", gap: 6,
          }}>
            {files.map((f, i) => (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "#f4f4f3", border: "1px solid #e5e5e4",
                borderRadius: 6, padding: "4px 8px", fontSize: 11,
                color: "#444", maxWidth: 220,
              }}>
                {f.file.type.startsWith("image") ? <ImageIcon size={12} /> : <FilePdf size={12} />}
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.file.name}
                </span>
                <button type="button" onClick={() => removeFile(i)}
                  data-testid={`remove-file-${i}`}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: 0 }}>
                  <X size={11} weight="bold" />
                </button>
              </span>
            ))}
            {attachError && (
              <span data-testid="chat-attach-error" style={{
                color: "#b91c1c", fontSize: 11, alignSelf: "center",
              }}>{attachError}</span>
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <input
            ref={fileInputRef}
            type="file"
            data-testid="chat-file-input"
            multiple
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={onPickFiles}
            style={{ display: "none" }}
          />
          <button type="button"
            data-testid="chat-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy || files.length >= MAX_FILES}
            title={files.length >= MAX_FILES ? "Max 4 files" : "Attach images or PDFs"}
            style={{
              background: "#f4f4f3", border: "1px solid #e5e5e4", borderRadius: 8,
              padding: "10px", cursor: busy || files.length >= MAX_FILES ? "not-allowed" : "pointer",
              opacity: busy || files.length >= MAX_FILES ? 0.5 : 1,
              color: "#555",
            }}>
            <Paperclip size={16} weight="bold" />
          </button>
          <textarea data-testid="chat-input"
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown} disabled={busy}
            placeholder={files.length ? "Add a note (optional) — Enter to send" : "Message the agent (Enter to send)…"}
            rows={1}
            style={{
              flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e5e4",
              fontSize: 13, fontFamily: "inherit", resize: "none", outline: "none",
              lineHeight: "20px", minHeight: 40, maxHeight: 120,
            }} />
          <button data-testid="chat-send-btn" onClick={send}
            disabled={busy || (!input.trim() && files.length === 0)}
            style={{
              background: "#323233", color: "#fff", border: "none", borderRadius: 8,
              padding: "10px 14px",
              cursor: busy || (!input.trim() && files.length === 0) ? "not-allowed" : "pointer",
              opacity: busy || (!input.trim() && files.length === 0) ? 0.5 : 1,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
            <PaperPlaneRight size={16} weight="bold" />
          </button>
        </div>
      </div>
      </>}
      <FormFavPanel token={token} onLogout={onLogout} chatCollapsed={chatCollapsed} />
      <AnalyticsModal open={analyticsOpen} onClose={() => setAnalyticsOpen(false)}
        token={token} onLogout={onLogout} />
      <MasterPromptModal open={promptOpen} onClose={() => setPromptOpen(false)}
        token={token} onLogout={onLogout} />
    </div>
  );
};

// ============== Public AdminPortal wrapper ==============

export const AdminPortal = ({ onDataChanged }) => {
  const [token, setToken] = useState(() => getStoredToken());
  const [showLogin, setShowLogin] = useState(false);
  const [showChat, setShowChat] = useState(false);

  // On mount, if token exists, validate it once
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, { headers: authHeaders(token) });
      } catch {
        if (!cancelled) {
          try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
          setToken(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleKeyClick = () => {
    if (token) setShowChat(true);
    else setShowLogin(true);
  };

  const handleLoginSuccess = (newToken) => {
    setToken(newToken);
    setShowLogin(false);
    setShowChat(true);
  };

  const handleLogout = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setToken(null);
    setShowChat(false);
  };

  return (
    <>
      <AdminKeyTrigger onClick={handleKeyClick} />
      <LoginModal open={showLogin} onClose={() => setShowLogin(false)}
        onSuccess={handleLoginSuccess} />
      <ChatPanel open={showChat && !!token} onClose={() => setShowChat(false)}
        token={token} onLogout={handleLogout} onDataChanged={onDataChanged} />
    </>
  );
};
